import React, { useEffect, useMemo, useRef, useState } from 'react'
import Header from '../../components/Header'
import { supabase } from '../../supabaseClient'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import { useRecipeAIHistory } from '../../hooks/useRecipeAIHistory'
import { authHeaders } from '../../utils/authFetch'

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

if (typeof document !== 'undefined' && !document.getElementById('scrollbar-hide-styles')) {
    const style = document.createElement('style')
    style.id = 'scrollbar-hide-styles'
    style.innerHTML = `
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    `
    document.head.appendChild(style)
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.teal} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const parseAISections = (text) => {
    if (!text) return []
    const lines = text.split('\n')
    const sections = []
    let current = null

    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const isHeader = trimmed.startsWith('### ')
        if (isHeader) {
            if (current) sections.push(current)
            current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
        } else if (/^[-•*]\s+/.test(trimmed)) {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
        } else {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed)
        }
    }
    if (current) sections.push(current)
    return sections
}

const RECIPE_SECTION_COLORS = {
    'RECIPE IDEAS': { color: C.teal, icon: '🥘' },
    'WHY THESE WORK': { color: C.amber, icon: '💡' },
}
const DEFAULT_SECTION_COLORS = [C.teal, C.amber, C.blue, C.purple, C.coral]

const renderBulletText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
            : part
    )
}

const MarkdownBlock = ({ text }) => {
    if (!text) return null
    const sections = parseAISections(text)
    if (!sections.length) return null

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sections.map((section, idx) => {
                const key = section.title?.toUpperCase().trim()
                const cfg = (key && RECIPE_SECTION_COLORS[key]) || {
                    color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
                    icon: '•',
                }
                return (
                    <div key={idx} style={{ borderRadius: 10, border: `1px solid ${cfg.color}28`, overflow: 'hidden' }}>
                        {section.title && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                                background: cfg.color + '10', borderBottom: `1px solid ${cfg.color}20`,
                            }}>
                                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                                <span style={{
                                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.07em', color: cfg.color,
                                }}>{section.title}</span>
                            </div>
                        )}
                        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {section.bullets.map((bullet, bIdx) => (
                                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 7 }} />
                                    <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{renderBulletText(bullet)}</span>
                                </div>
                            ))}
                            {section.bullets.length === 0 && (
                                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{section.title}</span>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const RecipeAISidebar = ({
    open, onClose, summary, recommendations, loading, error, onGenerate,
    hasData, history, historyLoading, onDeleteHistory,
    scope, onScopeChange, branches,
}) => {
    const [activeTab, setActiveTab] = useState("analysis")
    const [expandedId, setExpandedId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)

    const handleDelete = async (id) => {
        setDeletingId(id)
        await onDeleteHistory(id)
        if (expandedId === id) setExpandedId(null)
        setDeletingId(null)
    }

    const formatBranchName = (name) => name?.replace("Coffee Gus·to ", "") || ""

    return (
        <>
            {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 999 }} />}

            <div style={{
                position: "fixed", top: 0, right: 0, bottom: 0,
                width: "min(420px, 100vw)",
                background: "#fff", borderLeft: `1px solid ${C.border}`, zIndex: 1000,
                display: "flex", flexDirection: "column",
                transform: open ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
            }}>
                <div style={{
                    padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexShrink: 0, background: C.teal + "06",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%", background: C.teal,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
                            </svg>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Recipe Analyst</p>
                            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Stock-Aware · Cost-Efficient</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, color: C.muted }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.dark }}>
                    {[
                        { id: "analysis", label: "Suggestions" },
                        { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` },
                    ].map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                            flex: 1, padding: "10px 0", background: "none", border: "none",
                            borderBottom: activeTab === tab.id ? `2px solid ${C.teal}` : "2px solid transparent",
                            color: activeTab === tab.id ? C.teal : C.muted,
                            fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 400,
                            cursor: "pointer", marginBottom: -1,
                        }}>{tab.label}</button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
                    {activeTab === "analysis" && (
                        <>
                            {/* Scope selector — new */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>
                                    Scope
                                </label>
                                <select
                                    value={scope}
                                    onChange={(e) => onScopeChange(e.target.value)}
                                    disabled={loading}
                                    style={{
                                        width: "100%", padding: "8px 10px", borderRadius: 8,
                                        border: `1px solid ${C.border}`, fontSize: 12, color: C.text,
                                        background: "#fff", cursor: loading ? "not-allowed" : "pointer",
                                    }}
                                >
                                    <option value="all">All Branches</option>
                                    {(branches || []).map(b => (
                                        <option key={b.branch_id} value={b.branch_id}>
                                            {formatBranchName(b.name)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={onGenerate}
                                disabled={loading || !hasData}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 50, border: "none",
                                    background: loading || !hasData ? C.border : C.teal, color: "#fff",
                                    fontSize: 12, fontWeight: 600, cursor: loading || !hasData ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                                    marginBottom: summary ? 8 : 20,
                                }}
                            >
                                {loading ? "Generating ideas…" : summary ? "Regenerate Suggestions" : "Generate Suggestions"}
                            </button>

                            {summary && !loading && (
                                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                                    {["🤖 Gemini 2.5 Flash-Lite", "🥘 Grounded to your stock only", "⚠️ For reference only"].map(label => (
                                        <span key={label} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 50, border: `1px solid ${C.border}`, color: C.muted, background: C.dark }}>{label}</span>
                                    ))}
                                </div>
                            )}

                            {!hasData && (
                                <div style={{ padding: "10px 14px", borderRadius: 10, background: C.dark, border: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
                                    No underutilized ingredients detected yet for this branch.
                                </div>
                            )}

                            {error && (
                                <div style={{ background: C.coral + "15", border: `1px solid ${C.coral}44`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.coral, marginTop: 16 }}>
                                    {error}
                                </div>
                            )}

                            {summary && !loading && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div style={{ background: C.teal + "08", border: `1px solid ${C.teal}28`, borderRadius: 12, overflow: "hidden" }}>
                                        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`, background: C.teal + "10", display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 14 }}>🥘</span>
                                            <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                                                Recipe Ideas
                                            </p>
                                        </div>
                                        <div style={{ padding: "12px 14px" }}><MarkdownBlock text={summary} /></div>
                                    </div>

                                    {recommendations && (
                                        <div style={{ background: C.amber + "08", border: `1px solid ${C.amber}28`, borderRadius: 12, overflow: "hidden" }}>
                                            <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.amber}18`, background: C.amber + "10", display: "flex", alignItems: "center", gap: 8 }}>
                                                <span style={{ fontSize: 14 }}>💡</span>
                                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.amber }}>
                                                    Why These Work
                                                </p>
                                            </div>
                                            <div style={{ padding: "12px 14px" }}><MarkdownBlock text={recommendations} /></div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "history" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {historyLoading ? (
                                <Spinner height={160} />
                            ) : !history?.length ? (
                                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                                </div>
                            ) : history.map(entry => {
                                const isOpen = expandedId === entry.id
                                return (
                                    <div key={entry.id} style={{ borderRadius: 12, border: `1px solid ${isOpen ? C.teal + "44" : C.border}`, overflow: "hidden" }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: isOpen ? C.teal + "08" : C.dark, cursor: "pointer" }}
                                            onClick={() => setExpandedId(isOpen ? null : entry.id)}
                                        >
                                            <div>
                                                <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, background: C.teal + "15", borderRadius: 50, padding: "2px 8px" }}>
                                                    {entry.scope_label || "Recipe Suggestions"}
                                                </span>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {new Date(entry.generated_at).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                                                </p>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                                                disabled={deletingId === entry.id}
                                                style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 6px", cursor: "pointer", color: C.muted }}
                                            >✕</button>
                                        </div>
                                        {isOpen && (
                                            <div style={{ padding: "12px 14px" }}>
                                                <MarkdownBlock text={entry.summary} />
                                                {entry.recommendations && <div style={{ marginTop: 10 }}><MarkdownBlock text={entry.recommendations} /></div>}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

const RecipeAIFloatingButton = ({ onClick }) => (
    <button
        onClick={onClick}
        title="Open Recipe Analyst"
        style={{
            position: "fixed", bottom: 28, right: 28, width: 52, height: 52, borderRadius: "50%",
            background: C.teal, border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 18px rgba(13,148,136,0.35)", zIndex: 998,
        }}
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
        </svg>
    </button>
)

export default function InventoryPOS({ onGoToProfile }) {
    const [activeTab, setActiveTab] = useState('stock_levels')
    const [loading, setLoading] = useState(true)

    const [ingredientsPage, setIngredientsPage] = useState(1)
    const [recipesPage, setRecipesPage] = useState(1)
    const [posTransactionsPage, setPosTransactionsPage] = useState(1)
    const [branchStockPages, setBranchStockPages] = useState({})
    const [branchTxPages, setBranchTxPages] = useState({})
    const PAGE_SIZE = 10

    // Branches
    const [branches, setBranches] = useState([])
    const [branchesLoading, setBranchesLoading] = useState(true)
    const [selectedBranch, setSelectedBranch] = useState('all')
    const [branchBreakdownCollapsed, setBranchBreakdownCollapsed] = useState(true)
    const [uniqueItemsCollapsed, setUniqueItemsCollapsed] = useState(true)
    const [selectedBranchPill, setSelectedBranchPill] = useState('all')
    const [expandedBranchId, setExpandedBranchId] = useState(null)
    const [branchSubTab, setBranchSubTab] = useState({})

    const [branchStockLevels, setBranchStockLevels] = useState([])
    const [branchTransactions, setBranchTransactions] = useState({})
    const [stockLevelLoading, setStockLevelLoading] = useState(false)
    const [branchStockSearch, setBranchStockSearch] = useState({})
    const [branchStockStatusFilter, setBranchStockStatusFilter] = useState({})
    const [branchRestockStatusFilter, setBranchRestockStatusFilter] = useState({})
    const [stockLevelSearch, setStockLevelSearch] = useState('')
    const [stockLevelBranchFilter, setStockLevelBranchFilter] = useState('all')
    const [stockStatusFilter, setStockStatusFilter] = useState('all')

    // Inventory Management
    const [ingredients, setIngredients] = useState([])
    const [menuItems, setMenuItems] = useState([])
    const [recipeRows, setRecipeRows] = useState([])
    const [initialIngredientData, setInitialIngredientData] = useState(null)
    const [initialRecipeFormRows, setInitialRecipeFormRows] = useState([])

    const [ingredientSearch, setIngredientSearch] = useState('')
    const [menuItemSearch, setMenuItemSearch] = useState('')

    const [transactionSearch, setTransactionSearch] = useState('')
    const [branchTxSearch, setBranchTxSearch] = useState({})
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    const [branchRestockRequests, setBranchRestockRequests] = useState({})
    const [branchRestockPages, setBranchRestockPages] = useState({})
    const [reviewingRestockId, setReviewingRestockId] = useState(null)
    const [branchStockCountReview, setBranchStockCountReview] = useState({})
    const [stockCountReviewDates, setStockCountReviewDates] = useState({})
    const [branchStockCountPages, setBranchStockCountPages] = useState({})

    const [recipeItemSearch, setRecipeItemSearch] = useState('')
    const [ingredientDropdownSearch, setIngredientDropdownSearch] = useState({})
    const [openIngredientDropdownIndex, setOpenIngredientDropdownIndex] = useState(null)
    const ingredientDropdownContainerRef = useRef(null)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
    const ingredientInputRefs = useRef({})
    const dragScrollRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false })

    const [showAddIngredientModal, setShowAddIngredientModal] = useState(false)
    const [showAssignRecipeModal, setShowAssignRecipeModal] = useState(false)
    const [showEditIngredientModal, setShowEditIngredientModal] = useState(false)
    const [editingIngredient, setEditingIngredient] = useState(null)

    const [selectedMenuItem, setSelectedMenuItem] = useState(null)
    const [archivedIngredients, setArchivedIngredients] = useState([])
    const [showArchivedModal, setShowArchivedModal] = useState(false)
    const [archivedSearch, setArchivedSearch] = useState('')
    const [archivedCategoryFilter, setArchivedCategoryFilter] = useState('All')
    const [archivedPage, setArchivedPage] = useState(1)

    const [ingredientCategoryFilter, setIngredientCategoryFilter] = useState('All')
    const [recipeCategoryFilter, setRecipeCategoryFilter] = useState('All')
    const [recipeSetFilter, setRecipeSetFilter] = useState('all') // 'all' | 'set' | 'unset'
    const [recipeClassificationFilter, setRecipeClassificationFilter] = useState('All')
    const [availableRecipeClassifications, setAvailableRecipeClassifications] = useState([])
    const [itemsWithRecipeNames, setItemsWithRecipeNames] = useState(new Set())
    const [recipeStatusLoading, setRecipeStatusLoading] = useState(false)

    // Ingredient CSV Import
    const [showIngredientImportModal, setShowIngredientImportModal] = useState(false)
    const [ingredientImportFile, setIngredientImportFile] = useState(null)
    const [ingredientImportPreview, setIngredientImportPreview] = useState([])
    const [ingredientImportErrors, setIngredientImportErrors] = useState([])
    const [ingredientImporting, setIngredientImporting] = useState(false)

    // Recipe CSV Import
    const [showRecipeImportModal, setShowRecipeImportModal] = useState(false)
    const [recipeImportFile, setRecipeImportFile] = useState(null)
    const [recipeImportPreview, setRecipeImportPreview] = useState([])
    const [recipeImportErrors, setRecipeImportErrors] = useState([])
    const [recipeImporting, setRecipeImporting] = useState(false)

    const [ingredientFormData, setIngredientFormData] = useState({
        name: '',
        unit: '',
        category: '',
        package_label: '',
        stock_per_package: '',
        package_price: ''
    })

    const [recipeFormRows, setRecipeFormRows] = useState([
        { ingredient_id: '', quantity_used: '' }
    ])

    // POS Transactions
    const [posTransactions, setPosTransactions] = useState([])
    const [posLoading, setPosLoading] = useState(false)

    const [selectedBranchFilter, setSelectedBranchFilter] = useState('all')
    const [dateFilter, setDateFilter] = useState('all')
    const [orderStatusFilter, setOrderStatusFilter] = useState('all')

    const [branchRestockPredictions, setBranchRestockPredictions] = useState({})
    const [restockPredictionSearch, setRestockPredictionSearch] = useState({})
    const [branchRestockPredictionPages, setBranchRestockPredictionPages] = useState({})
    const [recalculatingPredictions, setRecalculatingPredictions] = useState({})

    const [globalRestockBufferPercent, setGlobalRestockBufferPercent] = useState(20)
    const [bufferPercentInput, setBufferPercentInput] = useState('20')
    const [savingBufferPercent, setSavingBufferPercent] = useState(false)

    // Recipe AI
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false)
    const [aiSummary, setAiSummary] = useState(null)
    const [aiRecommendations, setAiRecommendations] = useState(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [underutilizedIngredients, setUnderutilizedIngredients] = useState([])
    const [underutilizedLoading, setUnderutilizedLoading] = useState(false)
    const [recipeAIScope, setRecipeAIScope] = useState('all')

    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useRecipeAIHistory(
        recipeAIScope === 'all' ? null : recipeAIScope
    )

    const navigate = useNavigate();

    const FIELD_LIMITS = {
        name: 100,
        unit: 20,
        category: 50,
        package_label: 50
    }

    const handleDragMouseDown = (e) => {
        const el = e.currentTarget
        dragScrollRef.current = {
            isDown: true,
            startX: e.pageX - el.offsetLeft,
            scrollLeft: el.scrollLeft,
            moved: false
        }
        el.classList.add('cursor-grabbing')
    }

    const handleDragMouseMove = (e) => {
        const state = dragScrollRef.current
        if (!state.isDown) return
        e.preventDefault()
        const el = e.currentTarget
        const x = e.pageX - el.offsetLeft
        const walk = x - state.startX
        if (Math.abs(walk) > 3) state.moved = true
        el.scrollLeft = state.scrollLeft - walk
    }

    const handleDragMouseUpOrLeave = (e) => {
        dragScrollRef.current.isDown = false
        e.currentTarget.classList.remove('cursor-grabbing')
    }

    const handleHorizontalWheelScroll = (e) => {
        if (e.deltaY === 0) return
        e.currentTarget.scrollLeft += e.deltaY
        e.preventDefault()
    }

    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const API = `${BASE_URL}/api/inventory-pos`

    // === Excel Helpers ────────────────────────────────────────────────────────

    const loadExcelJS = async () => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script")
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js"
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }
        return window.ExcelJS
    }

    const cellToString = (cell) => {
        const v = cell.value
        if (v === null || v === undefined) return ''
        if (typeof v === 'object') {
            if (v.richText) return v.richText.map(rt => rt.text).join('')
            if (v.text) return String(v.text)
            if (v.result !== undefined) return String(v.result)
            return ''
        }
        return String(v)
    }

    const downloadExcelTemplate = async (filename, sheetName, headers, sampleRows) => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        const ws = wb.addWorksheet(sheetName)
        ws.columns = headers.map(h => ({ header: h, width: 24 }))
        const headerRow = ws.getRow(1)
        headerRow.eachCell(cell => {
            cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF111827" } }
            cell.alignment = { vertical: "middle", horizontal: "center" }
        })
        headerRow.height = 22
        sampleRows.forEach(row => ws.addRow(row))
        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
    }

    useEffect(() => {
        const init = async () => {
            const branchList = await fetchBranches()
            fetchIngredients()
            fetchArchivedIngredients()
            fetchMenuItems()
            fetchPOSTransactions()
            fetchBranchStockLevels(branchList)
            fetchGlobalRestockBufferPercent()
        }
        init()
    }, [])

    useEffect(() => {
        setIngredientsPage(1)
    }, [ingredientSearch, ingredientCategoryFilter])

    useEffect(() => {
        setArchivedPage(1)
    }, [archivedSearch, archivedCategoryFilter])

    useEffect(() => {
        setRecipesPage(1)
    }, [recipeItemSearch, recipeCategoryFilter, recipeClassificationFilter, recipeSetFilter])

    useEffect(() => {
        if (menuItems.length > 0) {
            fetchRecipeStatus()
        }
    }, [menuItems])

    useEffect(() => {
        setPosTransactionsPage(1)
    }, [transactionSearch, dateFilter, selectedBranchFilter, orderStatusFilter, customStart, customEnd])

    useEffect(() => {
        setBranchStockPages({})
    }, [branchStockSearch])

    useEffect(() => {
        setBranchTxPages({})
    }, [branchTxSearch])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                ingredientDropdownContainerRef.current &&
                !ingredientDropdownContainerRef.current.contains(event.target)
            ) {
                setOpenIngredientDropdownIndex(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const normalizeIngredientData = (data) => ({
        name: (data.name || '').trim(),
        unit: (data.unit || '').trim(),
        category: (data.category || '').trim(),
        package_label: (data.package_label || '').trim(),
        stock_per_package: data.stock_per_package === '' || data.stock_per_package === null
            ? null
            : Number(data.stock_per_package),
        package_price: data.package_price === '' || data.package_price === null
            ? null
            : Number(data.package_price)
    })

    const hasIngredientChanges = useMemo(() => {
        if (!initialIngredientData) return false

        const normalizedInitial = normalizeIngredientData(initialIngredientData)
        const normalizedCurrent = normalizeIngredientData(ingredientFormData)

        return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent)
    }, [initialIngredientData, ingredientFormData])

    const fetchBranches = async () => {
        try {
            setBranchesLoading(true)
            const { data, error } = await supabase
                .from("branches")
                .select("branch_id, name, status")
                .neq("status", "inactive")
                .order("name")
            if (error) throw error
            const EXCLUDED = ['Alea Residences', 'Asteria Residences', 'Calathea Place', 'HAU']
            const filtered = (data ?? []).filter(b => !EXCLUDED.some(ex => b.name.includes(ex)))
            setBranches(filtered)
            return filtered
        } catch (err) {
            console.error('Error fetching branches:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Load Branches', text: err.message, confirmButtonColor: '#A0826D' })
            return []
        } finally {
            setBranchesLoading(false)
        }
    }

    const fetchGlobalRestockBufferPercent = async () => {
        try {
            const res = await fetch(`${API}/buffer-percent`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch buffer percent')
            const data = await res.json()
            setGlobalRestockBufferPercent(Number(data.restock_buffer_percent))
            setBufferPercentInput(String(data.restock_buffer_percent))
        } catch (err) {
            console.error('Error fetching restock buffer percent:', err)
        }
    }

    const handleSaveGlobalBufferPercent = async () => {
        const value = parseFloat(bufferPercentInput)
        if (isNaN(value) || value < 0 || value > 500) {
            Swal.fire({ icon: 'warning', title: 'Invalid Percentage', text: 'Enter a value between 0 and 500.', confirmButtonColor: '#A0826D' })
            return
        }
        setSavingBufferPercent(true)
        try {
            const res = await fetch(`${API}/buffer-percent`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ value }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to save')
            setGlobalRestockBufferPercent(value)
            Swal.fire({ icon: 'success', title: 'Buffer % Updated', text: 'New restock suggestions will use this percentage. Recalculate per branch to apply it.', confirmButtonColor: '#A0826D', timer: 2200 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Save', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setSavingBufferPercent(false)
        }
    }

    const fetchIngredients = async () => {
        try {
            const res = await fetch(`${API}/ingredients`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch ingredients')
            setIngredients(await res.json())
        } catch (err) {
            console.error('Error fetching ingredients:', err)
            setIngredients([])
        }
    }

    const fetchMenuItems = async () => {
        try {
            const res = await fetch(`${API}/menu-items`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch menu items')
            const data = await res.json()
            setMenuItems((data || []).filter(item => !item.is_archived))
        } catch (err) {
            console.error('Error fetching menu items:', err)
            setMenuItems([])
        }
    }

    const fetchRecipeStatus = async () => {
        if (menuItems.length === 0) return
        try {
            setRecipeStatusLoading(true)
            const ids = menuItems.map(m => m.item_id).join(',')
            const res = await fetch(`${API}/recipes/export?itemIds=${ids}`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch recipe status')
            const rows = await res.json()
            setItemsWithRecipeNames(new Set(rows.map(r => r.item_name)))
        } catch (err) {
            console.error('Error fetching recipe status:', err)
        } finally {
            setRecipeStatusLoading(false)
        }
    }


    const fetchRecipeRows = async (itemId) => {
        try {
            const res = await fetch(`${API}/menu-items/${itemId}/recipe`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch recipe rows')
            setRecipeRows(await res.json())
        } catch (err) {
            console.error('Error fetching recipe rows:', err)
            setRecipeRows([])
        }
    }

    const fetchArchivedIngredients = async () => {
        try {
            const res = await fetch(`${API}/ingredients/archived`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch archived ingredients')
            setArchivedIngredients(await res.json())
        } catch (err) {
            console.error('Error fetching archived ingredients:', err)
            setArchivedIngredients([])
        }
    }

    const closeArchivedModal = () => {
        setShowArchivedModal(false)
        setArchivedSearch('')
        setArchivedCategoryFilter('All')
        setArchivedPage(1)
    }

    const fetchBranchTransactions = async (branchId) => {
        if (branchTransactions[branchId]?.data) return
        setBranchTransactions(prev => ({ ...prev, [branchId]: { data: null, loading: true } }))
        try {
            const res = await fetch(`${API}/branches/${branchId}/transactions`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch branch transactions')
            const formatted = await res.json()
            setBranchTransactions(prev => ({ ...prev, [branchId]: { data: formatted, loading: false } }))
        } catch (err) {
            console.error('Error fetching branch transactions:', err)
            setBranchTransactions(prev => ({ ...prev, [branchId]: { data: [], loading: false } }))
        }
    }

    const fetchBranchRestockRequests = async (branchId) => {
        setBranchRestockRequests(prev => ({ ...prev, [branchId]: { data: null, loading: true } }))
        try {
            const res = await fetch(`${API}/branches/${branchId}/restock-requests`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch restock requests')
            const formatted = await res.json()
            setBranchRestockRequests(prev => ({ ...prev, [branchId]: { data: formatted, loading: false } }))
        } catch (err) {
            console.error('Error fetching restock requests:', err)
            setBranchRestockRequests(prev => ({ ...prev, [branchId]: { data: [], loading: false } }))
        }
    }

    const fetchBranchStockCountReview = async (branchId, reviewDate) => {
        setBranchStockCountReview(prev => ({ ...prev, [branchId]: { data: null, loading: true } }))
        try {
            const res = await fetch(`${API}/branches/${branchId}/stock-count-review?date=${reviewDate}`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch stock count review')
            const review = await res.json()
            setBranchStockCountReview(prev => ({ ...prev, [branchId]: { data: review, loading: false } }))
        } catch (err) {
            console.error('Error fetching branch stock count review:', err)
            setBranchStockCountReview(prev => ({ ...prev, [branchId]: { data: [], loading: false } }))
        }
    }

    const fetchBranchRestockPredictions = async (branchId) => {
        setBranchRestockPredictions(prev => ({ ...prev, [branchId]: { data: null, loading: true } }))
        try {
            const res = await fetch(`${API}/branches/${branchId}/restock-predictions`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch restock predictions')
            const formatted = await res.json()
            setBranchRestockPredictions(prev => ({ ...prev, [branchId]: { data: formatted, loading: false } }))
        } catch (err) {
            console.error('Error fetching restock predictions:', err)
            setBranchRestockPredictions(prev => ({ ...prev, [branchId]: { data: [], loading: false } }))
        }
    }

    const handleRecalculatePredictions = async (branchId) => {
        setRecalculatingPredictions(prev => ({ ...prev, [branchId]: true }))
        try {
            const res = await fetch(`${API}/branches/${branchId}/restock-predictions/recalculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ bufferPercent: globalRestockBufferPercent }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to recalculate')

            await fetchBranchRestockPredictions(branchId)
            setBranchRestockPredictionPages(prev => ({ ...prev, [branchId]: 1 }))

            Swal.fire({
                icon: 'success',
                title: 'Predictions Updated',
                text: `Recalculated restock suggestions for ${body.count} ingredient(s).`,
                confirmButtonColor: '#A0826D',
                timer: 2000,
            })
        } catch (err) {
            console.error('Error recalculating predictions:', err)
            Swal.fire({ icon: 'error', title: 'Recalculation Failed', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setRecalculatingPredictions(prev => ({ ...prev, [branchId]: false }))
        }
    }

    const handleApproveRestock = async (requestId, branchId, ingredientName) => {
        const result = await Swal.fire({
            icon: 'question', title: 'Approve Restock Request?',
            text: `Approve the restock request for "${ingredientName}"? The branch admin will be notified to receive the stock.`,
            showCancelButton: true, confirmButtonText: 'Approve', cancelButtonText: 'Cancel',
            confirmButtonColor: '#16a34a', cancelButtonColor: '#6b7280',
        })
        if (!result.isConfirmed) return

        setReviewingRestockId(requestId)
        try {
            const res = await fetch(`${API}/restock-requests/${requestId}/approve`, { method: 'PATCH', headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to approve')

            setBranchRestockRequests(prev => ({ ...prev, [branchId]: { ...prev[branchId], data: null, loading: true } }))
            await fetchBranchRestockRequests(branchId)
            Swal.fire({ icon: 'success', title: 'Request Approved', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Approve', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setReviewingRestockId(null)
        }
    }

    const handleRejectRestock = async (requestId, branchId, ingredientName) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Reject Restock Request?',
            text: `Reject the restock request for "${ingredientName}"?`,
            showCancelButton: true, confirmButtonText: 'Reject', cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280',
        })
        if (!result.isConfirmed) return

        setReviewingRestockId(requestId)
        try {
            const res = await fetch(`${API}/restock-requests/${requestId}/reject`, { method: 'PATCH', headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to reject')

            setBranchRestockRequests(prev => ({ ...prev, [branchId]: { ...prev[branchId], data: null, loading: true } }))
            await fetchBranchRestockRequests(branchId)
            Swal.fire({ icon: 'success', title: 'Request Rejected', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Reject', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setReviewingRestockId(null)
        }
    }

    const fetchPOSTransactions = async () => {
        try {
            const res = await fetch(`${API}/pos-transactions`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch POS transactions')
            setPosTransactions(await res.json())
        } catch (err) {
            console.error('Error fetching POS transactions:', err)
        } finally {
            setPosLoading(false)
        }
    }

    const fetchBranchStockLevels = async () => {
        try {
            setStockLevelLoading(true)
            const res = await fetch(`${API}/branch-stock-levels`, { headers: authHeaders() })
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body.error || `Failed to fetch stock levels (status ${res.status})`)
            }
            setBranchStockLevels(await res.json())
        } catch (err) {
            console.error('Error fetching branch stock levels:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Load Stock Levels', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setStockLevelLoading(false)
        }
    }

    const handleGenerateRecipeIdeas = async () => {
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiRecommendations(null)

        try {
            setUnderutilizedLoading(true)
            const underutilized = await fetchUnderutilizedIngredients(recipeAIScope)
            setUnderutilizedIngredients(underutilized)
            setUnderutilizedLoading(false)

            const isAllBranches = recipeAIScope === 'all'

            // Build stocked ingredients list — scoped or all, tagged with branch when aggregate
            const branchStocked = branchStockLevels
                .filter(item => (isAllBranches || item.branch_id === recipeAIScope) && item.current_stock > 0)
                .map(item => ({
                    name: item.ingredient_name,
                    unit: item.unit,
                    current_stock: item.current_stock,
                    category: item.category,
                    branch_name: isAllBranches ? item.branch_name : undefined,
                }))

            if (branchStocked.length === 0) {
                setAiError('No stocked ingredients found for the selected scope.')
                return
            }

            const scopeLabel = isAllBranches
                ? 'All Branches'
                : formatBranchName(branches.find(b => b.branch_id === recipeAIScope)?.name) || 'Branch'

            const payload = {
                stockedIngredients: branchStocked,
                underutilizedIngredients: underutilized,
                cuisineTheme: 'Filipino-inspired specialty coffee shop',
                existingMenuItems: menuItems.map(m => ({ name: m.name, category: m.category })),
                scopeLabel,
                isAllBranches,
            }

            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/summarize-recipes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(payload),
            })
            if (!res.ok) throw new Error('Failed to generate recipe suggestions')
            const { summary, recommendations } = await res.json()

            setAiSummary(summary)
            setAiRecommendations(recommendations)

            const { error } = await saveAnalysis({
                summary,
                recommendations,
                scopeLabel,
                dateRange: 'Current Stock Snapshot',
            })
            if (error) {
                console.error('Failed to save AI history:', error)
                setAiError(prev => prev ?? `Suggestions generated but not saved: ${error.message}`)
            }
        } catch (err) {
            setAiError(err.message || 'Failed to generate suggestions. Please try again.')
        } finally {
            setAiLoading(false)
            setUnderutilizedLoading(false)
        }
    }

    const fetchUnderutilizedIngredients = async (branchId) => {
        const isAllBranches = !branchId || branchId === 'all'
        const res = await fetch(`${API}/underutilized-ingredients?branchId=${isAllBranches ? 'all' : branchId} `, { headers: authHeaders() })
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to compute underutilized ingredients')
        return await res.json()
    }

    // === CRUD ─────────────────────────────────────────────────────────────────

    const handleToggleBranch = (branchId) => {
        if (expandedBranchId === branchId) {
            setExpandedBranchId(null)
        } else {
            setExpandedBranchId(branchId)
            setBranchSubTab(prev => ({
                ...prev,
                [branchId]: prev[branchId] || 'stock_levels'
            }))
            fetchBranchTransactions(branchId)
            fetchBranchRestockRequests(branchId)

            const today = new Date().toISOString().split('T')[0]
            setStockCountReviewDates(prev => ({ ...prev, [branchId]: prev[branchId] || today }))
            fetchBranchStockCountReview(branchId, stockCountReviewDates[branchId] || today)
            setBranchStockPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchTxPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchRestockPages(prev => ({ ...prev, [branchId]: 1 }))
        }
    }

    const handleAddIngredient = async () => {
        if (!ingredientFormData.name || !ingredientFormData.unit) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in ingredient name and unit.', confirmButtonColor: '#A0826D' })
            return
        }
        const normalizedIngredientName = ingredientFormData.name.trim().toLowerCase()
        const duplicateIngredient = ingredients.find(i => i.name?.trim().toLowerCase() === normalizedIngredientName)
        if (duplicateIngredient) {
            Swal.fire({ icon: 'warning', title: 'Duplicate Ingredient', text: `${ingredientFormData.name.trim()} already exists.`, confirmButtonColor: '#A0826D' })
            return
        }

        const stockPerPackage = parseFloat(ingredientFormData.stock_per_package)
        if (ingredientFormData.stock_per_package !== '' && (isNaN(stockPerPackage) || stockPerPackage < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Quantity per Package', text: 'Quantity per package must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }
        const packagePrice = parseFloat(ingredientFormData.package_price)
        if (ingredientFormData.package_price !== '' && (isNaN(packagePrice) || packagePrice < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Package Price', text: 'Package price must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        try {
            const res = await fetch(`${API}/ingredients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(ingredientFormData),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to add ingredient')

            setIngredientFormData({ name: '', unit: '', category: '', package_label: '', stock_per_package: '', package_price: '' })
            setShowAddIngredientModal(false)
            fetchIngredients()
            Swal.fire({ icon: 'success', title: 'Ingredient Added', text: `${ingredientFormData.name} has been successfully added.`, confirmButtonColor: '#A0826D' })
        } catch (err) {
            console.error('Error adding ingredient:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Add Ingredient', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const openEditIngredientModal = (ingredient) => {
        setEditingIngredient(ingredient)

        const formattedData = {
            name: ingredient.name || '',
            unit: ingredient.unit || '',
            category: ingredient.category || '',
            package_label: ingredient.package_label || '',
            stock_per_package: ingredient.stock_per_package ?? '',
            package_price: ingredient.package_price ?? ''
        }

        setIngredientFormData(formattedData)
        setInitialIngredientData(formattedData)
        setShowEditIngredientModal(true)
    }

    const handleUpdateIngredient = async () => {
        if (!editingIngredient) return
        if (!hasIngredientChanges) {
            Swal.fire({ icon: 'info', title: 'No Changes Detected', text: 'No changes were made.', confirmButtonColor: '#A0826D' })
            return
        }
        if (!ingredientFormData.name || !ingredientFormData.unit) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in ingredient name and unit.', confirmButtonColor: '#A0826D' })
            return
        }
        const normalizedIngredientName = ingredientFormData.name.trim().toLowerCase()
        const duplicateIngredient = ingredients.find(i => i.ingredient_id !== editingIngredient.ingredient_id && i.name?.trim().toLowerCase() === normalizedIngredientName)
        if (duplicateIngredient) {
            Swal.fire({ icon: 'warning', title: 'Duplicate Ingredient', text: `${ingredientFormData.name.trim()} already exists.`, confirmButtonColor: '#A0826D' })
            return
        }

        const stockPerPackage = parseFloat(ingredientFormData.stock_per_package)
        if (ingredientFormData.stock_per_package !== '' && (isNaN(stockPerPackage) || stockPerPackage < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Quantity per Package', text: 'Quantity per package must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }
        const packagePrice = parseFloat(ingredientFormData.package_price)
        if (ingredientFormData.package_price !== '' && (isNaN(packagePrice) || packagePrice < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Package Price', text: 'Package price must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        try {
            const res = await fetch(`${API}/ingredients/${editingIngredient.ingredient_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(ingredientFormData),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to update ingredient')

            setShowEditIngredientModal(false)
            setEditingIngredient(null)
            setIngredientFormData({ name: '', unit: '', category: '', package_label: '', stock_per_package: '', package_price: '' })
            fetchIngredients()
            Swal.fire({ icon: 'success', title: 'Ingredient Updated', text: `${ingredientFormData.name} has been successfully updated.`, confirmButtonColor: '#A0826D' })
            setInitialIngredientData(null)
        } catch (err) {
            console.error('Error updating ingredient:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Update Ingredient', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const handleDeleteIngredient = async (ingredient) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Archive Ingredient?', text: `Do you want to archive "${ingredient.name}"?`,
            showCancelButton: true, confirmButtonText: 'Archive', cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626', cancelButtonColor: '#A0826D',
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/ingredients/${ingredient.ingredient_id}/archive`, { method: 'PATCH', headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to archive')
            fetchIngredients()
            Swal.fire({ icon: 'success', title: 'Ingredient Archived', text: 'Ingredient has been archived successfully.', confirmButtonColor: '#A0826D', timer: 2000 })
        } catch (err) {
            console.error('Error archiving ingredient:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Archived Ingredient', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const addRecipeFormRow = () => {
        const hasIncompleteRow = recipeFormRows.some(
            row => !row.ingredient_id || !row.quantity_used
        )

        if (hasIncompleteRow) {
            Swal.fire({
                icon: 'warning',
                title: 'Incomplete Row',
                text: 'Please complete the current ingredient row before adding another one.',
                confirmButtonColor: '#A0826D'
            })
            return
        }

        setRecipeFormRows(prev => [
            ...prev,
            { ingredient_id: '', quantity_used: '' }
        ])
    }

    const updateRecipeFormRow = (index, field, value) => {
        setRecipeFormRows(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, [field]: value } : row
            )
        )
    }

    const removeRecipeFormRow = (index) => {
        setRecipeFormRows(prev => prev.filter((_, i) => i !== index))

        setIngredientDropdownSearch(prev => {
            const next = { ...prev }
            delete next[index]
            return next
        })

        if (openIngredientDropdownIndex === index) {
            setOpenIngredientDropdownIndex(null)
        }
    }

    const handleSaveRecipe = async () => {
        if (!selectedMenuItem) return
        if (!hasRecipeChanges) {
            Swal.fire({ icon: 'info', title: 'No Changes Detected', text: 'No changes were made to this recipe.', confirmButtonColor: '#A0826D' })
            return
        }

        const validRows = recipeFormRows.filter(row => row.ingredient_id && row.quantity_used)
        const selectedIngredientIds = validRows.map(row => String(row.ingredient_id))
        const hasDuplicateIngredients = new Set(selectedIngredientIds).size !== selectedIngredientIds.length
        if (hasDuplicateIngredients) {
            Swal.fire({ icon: 'warning', title: 'Duplicate Recipe Ingredient', text: 'You cannot use the same ingredient more than once in one menu item recipe.', confirmButtonColor: '#A0826D' })
            return
        }
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Recipe Rows', text: 'Please add at least one ingredient with quantity and unit.', confirmButtonColor: '#A0826D' })
            return
        }

        try {
            const res = await fetch(`${API}/menu-items/${selectedMenuItem.item_id}/recipe`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ rows: validRows }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to save recipe')

            setShowAssignRecipeModal(false)
            setSelectedMenuItem(null)
            setRecipeFormRows([{ ingredient_id: '', quantity_used: '' }])
            Swal.fire({ icon: 'success', title: 'Recipe Saved', text: `${selectedMenuItem.name} recipe has been updated.`, confirmButtonColor: '#A0826D', timer: 2000 })
            setInitialRecipeFormRows(validRows.map(row => ({ ingredient_id: row.ingredient_id, quantity_used: row.quantity_used })))
            fetchRecipeStatus()
        } catch (err) {
            console.error('Error saving recipe:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Save Recipe', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const openRecipeModal = async (item) => {
        setSelectedMenuItem(item)
        setShowAssignRecipeModal(true)
        try {
            const res = await fetch(`${API}/menu-items/${item.item_id}/recipe`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to load recipe')
            const data = await res.json()
            if (data && data.length > 0) {
                const mappedRows = data.map(row => ({ ingredient_id: row.ingredient_id, quantity_used: row.quantity_used }))
                setRecipeFormRows(mappedRows)
                setInitialRecipeFormRows(mappedRows)
            } else {
                const emptyRows = [{ ingredient_id: '', quantity_used: '' }]
                setRecipeFormRows(emptyRows)
                setInitialRecipeFormRows(emptyRows)
            }
        } catch (err) {
            console.error('Error loading recipe:', err)
            const emptyRows = [{ ingredient_id: '', quantity_used: '' }]
            setRecipeFormRows(emptyRows)
            setInitialRecipeFormRows(emptyRows)
        }
    }

    const handleReinstateIngredient = async (ingredient) => {
        try {
            const res = await fetch(`${API}/ingredients/${ingredient.ingredient_id}/reinstate`, { method: 'PATCH', headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to reinstate')
            fetchIngredients()
            fetchArchivedIngredients()
            Swal.fire({ icon: 'success', title: 'Ingredient Reinstated', confirmButtonColor: '#A0826D' })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Reinstate', text: err.message })
        }
    }


    const handleRemoveIngredient = async (ingredient) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Remove Ingredient?',
            html: `Are you sure you want to permanently remove <b>"${ingredient.name}"</b>?<br/><span class="text-sm text-red-600">This cannot be undone — recipes and branch stock records referencing this ingredient may be affected. Use Reinstate instead if you might need it again.</span>`,
            showCancelButton: true, confirmButtonText: 'Remove Permanently', cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626', cancelButtonColor: '#A0826D',
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/ingredients/${ingredient.ingredient_id}`, { method: 'DELETE', headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to remove')
            await fetchArchivedIngredients()
            await fetchIngredients()
            Swal.fire({ icon: 'success', title: `${ingredient.name} Removed`, text: `${ingredient.name} has been permanently removed.`, confirmButtonColor: '#A0826D', timer: 2000 })
        } catch (err) {
            console.error('Error removing ingredient:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Remove', text: err.message || 'An error occurred while removing the ingredient.', confirmButtonColor: '#A0826D' })
        }
    }

    // === Helpers ───────────────────────────────────────────────────────────────

    const normalizeRecipeRows = (rows = []) => {
        return rows
            .map(row => ({
                ingredient_id: String(row.ingredient_id || '').trim(),
                quantity_used: String(row.quantity_used || '').trim()
            }))
            .filter(row => row.ingredient_id || row.quantity_used)
            .sort((a, b) => a.ingredient_id.localeCompare(b.ingredient_id))
    }

    const hasRecipeChanges = useMemo(() => {
        const normalizedInitial = normalizeRecipeRows(initialRecipeFormRows)
        const normalizedCurrent = normalizeRecipeRows(recipeFormRows)

        return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent)
    }, [initialRecipeFormRows, recipeFormRows])

    const formatCurrency = (amount) => {
        return `₱${parseFloat(amount || 0).toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`
    }

    const formatDateTime = (value) => {
        if (!value) return 'N/A'
        return new Date(value).toLocaleString('en-PH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        })
    }

    const formatBranchName = (name) =>
        name?.replace("Coffee Gus·to ", "") || ""

    const filteredIngredients = [...ingredients]
        .filter((ingredient) => {
            const query = ingredientSearch.toLowerCase()
            const matchesSearch = (
                String(ingredient.name || '').toLowerCase().includes(query) ||
                String(ingredient.unit || '').toLowerCase().includes(query) ||
                String(ingredient.category || '').toLowerCase().includes(query) ||
                String(ingredient.package_label || '').toLowerCase().includes(query)
            )
            const matchesCategory =
                ingredientCategoryFilter === 'All' ||
                (ingredient.category || '—') === ingredientCategoryFilter
            return matchesSearch && matchesCategory
        })
        .sort((a, b) => a.name.localeCompare(b.name))

    const paginatedIngredients = filteredIngredients.slice(
        (ingredientsPage - 1) * PAGE_SIZE, ingredientsPage * PAGE_SIZE
    )

    const filteredMenuItems = [...menuItems]
        .filter((item) => {
            const query = recipeItemSearch.toLowerCase()
            const matchesSearch = (
                String(item.name || '').toLowerCase().includes(query) ||
                String(item.category || '').toLowerCase().includes(query) ||
                String(item.classification || '').toLowerCase().includes(query)
            )
            const matchesCategory =
                recipeCategoryFilter === 'All' || item.category === recipeCategoryFilter
            const matchesClassification =
                recipeClassificationFilter === 'All' || item.classification === recipeClassificationFilter
            const hasRecipe = itemsWithRecipeNames.has(item.name)
            const matchesRecipeStatus =
                recipeSetFilter === 'all' ||
                (recipeSetFilter === 'set' ? hasRecipe : !hasRecipe)
            return matchesSearch && matchesCategory && matchesClassification && matchesRecipeStatus
        })
        .sort((a, b) => a.name.localeCompare(b.name))

    const paginatedMenuItems = filteredMenuItems.slice(
        (recipesPage - 1) * PAGE_SIZE, recipesPage * PAGE_SIZE
    )

    const filteredStockLevels = branchStockLevels.filter(item => {
        const query = stockLevelSearch.toLowerCase()
        const matchesSearch =
            item.ingredient_name.toLowerCase().includes(query) ||
            item.branch_name.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.unit.toLowerCase().includes(query)
        const matchesBranch =
            stockLevelBranchFilter === 'all' || item.branch_id === stockLevelBranchFilter
        const matchesStatus =
            stockStatusFilter === 'all' || item.status === stockStatusFilter
        return matchesSearch && matchesBranch && matchesStatus
    })

    const filteredTransactions = posTransactions.filter(transaction => {
        const query = transactionSearch.toLowerCase()

        const matchesSearch =
            String(transaction.reference_number).toLowerCase().includes(query) ||
            String(transaction.payment_method).toLowerCase().includes(query) ||
            String(transaction.payment_status).toLowerCase().includes(query) ||
            String(transaction.branch_name).toLowerCase().includes(query)

        const matchesBranch =
            selectedBranchFilter === 'all' ||
            transaction.branch_id === selectedBranchFilter

        const matchesOrderStatus =
            orderStatusFilter === 'all' ||
            transaction.order_status === orderStatusFilter

        const transactionDate = new Date(transaction.created_at)
        const now = new Date()
        let matchesDate = true

        if (dateFilter === 'day') {
            matchesDate = transactionDate.toDateString() === now.toDateString()
        } else if (dateFilter === 'week') {
            const oneWeekAgo = new Date()
            oneWeekAgo.setDate(now.getDate() - 7)
            matchesDate = transactionDate >= oneWeekAgo
        } else if (dateFilter === 'month') {
            matchesDate =
                transactionDate.getMonth() === now.getMonth() &&
                transactionDate.getFullYear() === now.getFullYear()
        } else if (dateFilter === 'year') {
            matchesDate = transactionDate.getFullYear() === now.getFullYear()
        } else if (dateFilter === 'custom') {
            if (customStart && customEnd) {
                const from = new Date(customStart)
                const to = new Date(customEnd)
                to.setHours(23, 59, 59, 999)
                matchesDate = transactionDate >= from && transactionDate <= to
            }
        }

        return matchesSearch && matchesBranch && matchesOrderStatus && matchesDate
    })

    const paginatedPOSTransactions = filteredTransactions.slice(
        (posTransactionsPage - 1) * PAGE_SIZE, posTransactionsPage * PAGE_SIZE
    )

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 16, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                        Next →
                    </button>
                </div>
            </div>
        )
    }

    const parseIngredientExcel = async (file) => {
        const ExcelJS = await loadExcelJS()
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]
        if (!ws || ws.rowCount < 2) {
            throw new Error('Excel file must have a header row and at least one data row.')
        }

        const headerRow = ws.getRow(1)
        const colIndex = {}
        headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const h = cellToString(cell).trim().toLowerCase()
            if (h) colIndex[h] = colNumber
        })

        const required = ['name', 'unit']
        const missing = required.filter(r => !(r in colIndex))
        if (missing.length > 0) {
            throw new Error(`Missing required columns: ${missing.join(', ')}`)
        }

        const rows = []
        const errors = []
        const getVal = (row, key) => {
            const idx = colIndex[key]
            if (!idx) return ''
            return cellToString(row.getCell(idx)).trim()
        }

        for (let rowNum = 2; rowNum <= ws.rowCount; rowNum++) {
            const excelRow = ws.getRow(rowNum)
            if (!excelRow.hasValues) continue

            const rowErrors = []
            const name = getVal(excelRow, 'name')
            const unit = getVal(excelRow, 'unit')
            const category = getVal(excelRow, 'category') || null
            const package_label = getVal(excelRow, 'package_label') || null
            const stockRaw = getVal(excelRow, 'stock_per_package')
            const priceRaw = getVal(excelRow, 'package_price')
            const stock_per_package = stockRaw ? parseFloat(stockRaw) : null
            const package_price = priceRaw ? parseFloat(priceRaw) : null

            if (!name || name.length < 2) rowErrors.push('name must be at least 2 characters')
            if (name.length > 100) rowErrors.push('name must not exceed 100 characters')
            if (!unit) rowErrors.push('unit is required')
            if (unit.length > 20) rowErrors.push('unit must not exceed 20 characters')
            if (stock_per_package !== null && (isNaN(stock_per_package) || stock_per_package < 0)) rowErrors.push('stock_per_package must be 0 or greater')
            if (package_price !== null && (isNaN(package_price) || package_price < 0)) rowErrors.push('package_price must be 0 or greater')

            if (rowErrors.length > 0) errors.push({ row: rowNum, name: name || `Row ${rowNum}`, errors: rowErrors })
            rows.push({ name, unit, category, package_label, stock_per_package, package_price, _rowNum: rowNum, _hasError: rowErrors.length > 0 })
        }

        return { rows, errors }
    }

    const handleIngredientImportFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setIngredientImportFile(file)
        setIngredientImportPreview([])
        setIngredientImportErrors([])
        try {
            const { rows, errors } = await parseIngredientExcel(file)
            setIngredientImportPreview(rows)
            setIngredientImportErrors(errors)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Invalid Excel File', text: err.message, confirmButtonColor: '#A0826D' })
            setIngredientImportFile(null)
        }
    }


    const handleIngredientImport = async () => {
        const validRows = ingredientImportPreview.filter(r => !r._hasError)
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Valid Rows', text: 'Fix all errors before importing.', confirmButtonColor: '#A0826D' })
            return
        }
        try {
            setIngredientImporting(true)
            const res = await fetch(`${API}/ingredients/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ rows: validRows }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Import failed')

            const { inserted, duplicates } = body
            await fetchIngredients()
            Swal.fire({
                icon: inserted > 0 ? 'success' : 'warning',
                title: inserted > 0 ? `${inserted} Ingredient${inserted !== 1 ? 's' : ''} Imported!` : 'Nothing Imported',
                html: duplicates.length > 0 ? `<p>${duplicates.length} duplicate(s) skipped: <em>${duplicates.slice(0, 5).join(', ')}${duplicates.length > 5 ? '...' : ''}</em></p>` : undefined,
                confirmButtonColor: '#A0826D',
                timer: 3000,
            })
            if (duplicates.length === 0) {
                setShowIngredientImportModal(false)
                setIngredientImportFile(null)
                setIngredientImportPreview([])
                setIngredientImportErrors([])
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setIngredientImporting(false)
        }
    }

    const parseRecipeExcel = async (file) => {
        const ExcelJS = await loadExcelJS()
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]
        if (!ws || ws.rowCount < 2) {
            throw new Error('Excel file must have a header row and at least one data row.')
        }

        const headerRow = ws.getRow(1)
        const colIndex = {}
        headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const h = cellToString(cell).trim().toLowerCase()
            if (h) colIndex[h] = colNumber
        })

        const required = ['item_name', 'ingredient_name', 'quantity_used']
        const missing = required.filter(r => !(r in colIndex))
        if (missing.length > 0) {
            throw new Error(`Missing required columns: ${missing.join(', ')}`)
        }

        const rows = []
        const errors = []
        const getVal = (row, key) => {
            const idx = colIndex[key]
            if (!idx) return ''
            return cellToString(row.getCell(idx)).trim()
        }

        for (let rowNum = 2; rowNum <= ws.rowCount; rowNum++) {
            const excelRow = ws.getRow(rowNum)
            if (!excelRow.hasValues) continue

            const rowErrors = []
            const item_name = getVal(excelRow, 'item_name')
            const ingredient_name = getVal(excelRow, 'ingredient_name')
            const quantity_used = parseFloat(getVal(excelRow, 'quantity_used'))

            if (!item_name) rowErrors.push('item_name is required')
            if (!ingredient_name) rowErrors.push('ingredient_name is required')
            if (isNaN(quantity_used) || quantity_used <= 0) rowErrors.push('quantity_used must be a positive number')

            if (rowErrors.length > 0) errors.push({ row: rowNum, name: `${item_name} + ${ingredient_name}` || `Row ${rowNum}`, errors: rowErrors })
            rows.push({ item_name, ingredient_name, quantity_used, _rowNum: rowNum, _hasError: rowErrors.length > 0 })
        }

        return { rows, errors }
    }

    const handleRecipeImportFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setRecipeImportFile(file)
        setRecipeImportPreview([])
        setRecipeImportErrors([])
        try {
            const { rows, errors } = await parseRecipeExcel(file)
            setRecipeImportPreview(rows)
            setRecipeImportErrors(errors)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Invalid Excel File', text: err.message, confirmButtonColor: '#A0826D' })
            setRecipeImportFile(null)
        }
    }

    const handleRecipeImport = async () => {
        const validRows = recipeImportPreview.filter(r => !r._hasError)
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Valid Rows', text: 'Fix all errors before importing.', confirmButtonColor: '#A0826D' })
            return
        }
        try {
            setRecipeImporting(true)
            const res = await fetch(`${API}/recipes/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ rows: validRows }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Import failed')

            const { inserted, skipped } = body
            Swal.fire({
                icon: inserted > 0 ? 'success' : 'warning',
                title: inserted > 0 ? `${inserted} Recipe Row${inserted !== 1 ? 's' : ''} Imported!` : 'Nothing Imported',
                html: skipped.length > 0 ? `<p class="text-left text-sm">${skipped.length} row(s) skipped:<br/><em>${skipped.slice(0, 5).join('<br/>')}${skipped.length > 5 ? '...' : ''}</em></p>` : undefined,
                confirmButtonColor: '#A0826D',
                timer: skipped.length > 0 ? undefined : 3000,
            })
            fetchRecipeStatus()
            if (skipped.length === 0) {
                setShowRecipeImportModal(false)
                setRecipeImportFile(null)
                setRecipeImportPreview([])
                setRecipeImportErrors([])
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setRecipeImporting(false)
        }
    }

    const exportIngredientsExcel = async () => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const ws = wb.addWorksheet("Ingredients")
        ws.columns = [{ width: 26 }, { width: 14 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }]

        const headerRow = ws.getRow(1)
        headerRow.values = ["name", "unit", "category", "package_label", "stock_per_package", "package_price"]
        headerRow.height = 22
            ;[1, 2, 3, 4, 5, 6].forEach(ci => {
                const cell = headerRow.getCell(ci)
                cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFA0826D" } }
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
                cell.border = { bottom: { style: "medium", color: { argb: "FFD97706" } } }
            })

        filteredIngredients.forEach((ing, i) => {
            const row = ws.getRow(2 + i)
            row.height = 18
            row.getCell(1).value = ing.name || ''
            row.getCell(2).value = ing.unit || ''
            row.getCell(3).value = ing.category || ''
            row.getCell(4).value = ing.package_label || ''
            row.getCell(5).value = ing.stock_per_package ?? ''
            row.getCell(6).value = ing.package_price ?? ''
            if (ing.package_price !== null && ing.package_price !== undefined) {
                row.getCell(6).numFmt = '0.00'
                row.getCell(6).alignment = { horizontal: "right" }
            }

            if (i % 2 === 0) {
                [1, 2, 3, 4, 5, 6].forEach(ci => {
                    row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9FAFB" } }
                })
            }
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `ingredients_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }

    const exportRecipesExcel = async () => {
        try {
            const visibleItemIds = filteredMenuItems.map(i => i.item_id)
            if (visibleItemIds.length === 0) return

            const res = await fetch(`${API}/recipes/export?itemIds=${visibleItemIds.join(',')}`, { headers: authHeaders() })
            if (!res.ok) throw new Error((await res.json()).error || 'Failed to export')
            const rows = await res.json()

            if (rows.length === 0) {
                Swal.fire({ icon: 'info', title: 'No Recipes to Export', text: 'None of the visible menu items have recipes set.', confirmButtonColor: '#A0826D' })
                return
            }

            // Everything below (ExcelJS workbook building) is unchanged from your original function.
            const ExcelJS = await loadExcelJS()
            const wb = new ExcelJS.Workbook()
            wb.creator = "Coffee Gus·to"
            wb.created = new Date()

            const ws = wb.addWorksheet("Recipes")
            ws.columns = [{ width: 28 }, { width: 28 }, { width: 16 }, { width: 14 }]

            const headerRow = ws.getRow(1)
            headerRow.values = ["item_name", "ingredient_name", "quantity_used", "unit"]
            headerRow.height = 22
                ;[1, 2, 3, 4].forEach(ci => {
                    const cell = headerRow.getCell(ci)
                    cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFA0826D" } }
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
                    cell.border = { bottom: { style: "medium", color: { argb: "FFD97706" } } }
                })

            rows.forEach((r, i) => {
                const row = ws.getRow(2 + i)
                row.height = 18
                row.getCell(1).value = r.item_name
                row.getCell(2).value = r.ingredient_name
                row.getCell(3).value = r.quantity_used
                row.getCell(4).value = r.unit
                if (i % 2 === 0) {
                    [1, 2, 3, 4].forEach(ci => {
                        row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9FAFB" } }
                    })
                }
            })

            const buffer = await wb.xlsx.writeBuffer()
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `recipes_export_${new Date().toISOString().slice(0, 10)}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Export Failed', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <Header
                title="Inventory Management"
                subtitle="Super admin overview of branch menu assignments and POS visibility"
                onGoToProfile={onGoToProfile}
            />

            <div className="p-8">

                {/* Integration status */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="text-blue-600">
                        <path fill="currentColor" d="m9 20.42l-6.21-6.21l2.83-2.83L9 14.77l9.88-9.89l2.83 2.83z" />
                    </svg>
                    <div className="flex-1">
                        <p className="font-semibold text-blue-900">POS Integration Status</p>
                        <p className="text-sm text-blue-700 flex items-center gap-1.5">
                            Connected to {branchesLoading ? (
                                <SkeletonBlock className="w-4 h-3" />
                            ) : (
                                <>{branches.length} branch{branches.length !== 1 ? 'es' : ''}</>
                            )}
                            • Super admin summary mode
                        </p>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3 flex-wrap mb-6">
                    <div>
                        <p className="text-sm font-semibold text-gray-800">Global Restock Buffer %</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Applied to weekly usage when calculating suggested restock quantities across all branches. Click "Recalculate" per branch to apply changes.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <input
                            type="number"
                            min="0"
                            max="500"
                            step="1"
                            value={bufferPercentInput}
                            onChange={(e) => setBufferPercentInput(e.target.value)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <span className="text-sm text-gray-500">%</span>
                        <button
                            onClick={handleSaveGlobalBufferPercent}
                            disabled={savingBufferPercent || parseFloat(bufferPercentInput) === globalRestockBufferPercent}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${savingBufferPercent || parseFloat(bufferPercentInput) === globalRestockBufferPercent
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'
                                }`}
                        >
                            {savingBufferPercent ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 mb-6 overflow-x-auto scrollbar-hide"
                    onMouseDown={handleDragMouseDown}
                    onMouseMove={handleDragMouseMove}
                    onMouseUp={handleDragMouseUpOrLeave}
                    onMouseLeave={handleDragMouseUpOrLeave}
                    onWheel={handleHorizontalWheelScroll}
                >

                    <button
                        onClick={() => setActiveTab('stock_levels')}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'stock_levels'
                            ? 'bg-[#A0826D] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                    >
                        Branch Stock Levels
                    </button>

                    <button
                        onClick={() => setActiveTab('ingredients')}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'ingredients'
                            ? 'bg-[#A0826D] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                    >
                        Ingredients
                    </button>

                    <button
                        onClick={() => setActiveTab('recipes')}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'recipes'
                            ? 'bg-[#A0826D] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                    >
                        Menu Item Recipes
                    </button>

                    <button
                        onClick={() => setActiveTab('pos')}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'pos'
                            ? 'bg-[#A0826D] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                    >
                        POS Transactions
                    </button>
                </div>

                {/* Branch Stock Levels Tab */}
                {activeTab === 'stock_levels' && (
                    <div className="space-y-4">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Branch Stock Overview</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Click a branch card to view stock levels and transactions
                                </p>
                            </div>
                            <button
                                onClick={fetchBranchStockLevels}
                                className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b] self-start sm:self-auto"
                            >
                                ↻ Refresh All
                            </button>
                        </div>

                        {/* Summary pills */}
                        <div className="flex gap-3 flex-wrap">
                            {[
                                { label: 'In Stock', key: 'in_stock', color: 'bg-green-100 text-green-700' },
                                { label: 'Low Stock', key: 'low_stock', color: 'bg-orange-100 text-orange-700' },
                                { label: 'Out of Stock', key: 'out_of_stock', color: 'bg-red-100 text-red-700' },
                            ].map(({ label, key, color }) => (
                                <span key={key} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${color}`}>
                                    {label}:
                                    {stockLevelLoading
                                        ? <SkeletonBlock className="w-5 h-3" />
                                        : branchStockLevels.filter(i => i.status === key).length}
                                </span>
                            ))}
                        </div>

                        {/* Branch Cards */}
                        {stockLevelLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="text-center">
                                    <div className="w-8 h-8 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                    <p className="text-sm text-gray-500">Loading stock levels...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {[...branches].sort((a, b) => {
                                    const aItems = branchStockLevels.filter(i => i.branch_id === a.branch_id)
                                    const bItems = branchStockLevels.filter(i => i.branch_id === b.branch_id)
                                    const aOut = aItems.filter(i => i.status === 'out_of_stock').length
                                    const bOut = bItems.filter(i => i.status === 'out_of_stock').length
                                    const aLow = aItems.filter(i => i.status === 'low_stock').length
                                    const bLow = bItems.filter(i => i.status === 'low_stock').length

                                    // Sort out of stock first, then low stock, then alphabetical
                                    if (aOut !== bOut) return bOut - aOut
                                    if (aLow !== bLow) return bLow - aLow
                                    return formatBranchName(a.name).localeCompare(formatBranchName(b.name))
                                }).map(branch => {
                                    const branchItems = branchStockLevels.filter(i => i.branch_id === branch.branch_id)
                                    const outCount = branchItems.filter(i => i.status === 'out_of_stock').length
                                    const lowCount = branchItems.filter(i => i.status === 'low_stock').length
                                    const inCount = branchItems.filter(i => i.status === 'in_stock').length
                                    const isExpanded = expandedBranchId === branch.branch_id
                                    const activeSubTab = branchSubTab[branch.branch_id] || 'stock_levels'
                                    const stockSearch = branchStockSearch[branch.branch_id] || ''
                                    const stockStatus = branchStockStatusFilter[branch.branch_id] || 'all'
                                    const txSearch = branchTxSearch[branch.branch_id] || ''
                                    const txData = branchTransactions[branch.branch_id]

                                    const filteredBranchStock = branchItems.filter(item => {
                                        const q = stockSearch.toLowerCase()
                                        const matchesSearch = (
                                            item.ingredient_name.toLowerCase().includes(q) ||
                                            item.category.toLowerCase().includes(q) ||
                                            item.unit.toLowerCase().includes(q)
                                        )
                                        const matchesStatus = stockStatus === 'all' || item.status === stockStatus
                                        return matchesSearch && matchesStatus
                                    })
                                    const filteredBranchTx = (txData?.data || []).filter(tx => {
                                        const q = txSearch.toLowerCase()
                                        return (
                                            tx.ingredient_name.toLowerCase().includes(q) ||
                                            tx.transaction_type.toLowerCase().includes(q) ||
                                            tx.reference_type.toLowerCase().includes(q) ||
                                            tx.notes.toLowerCase().includes(q)
                                        )
                                    })

                                    const branchStockPage = branchStockPages[branch.branch_id] || 1
                                    const branchTxPage = branchTxPages[branch.branch_id] || 1
                                    const paginatedBranchStock = filteredBranchStock.slice((branchStockPage - 1) * PAGE_SIZE, branchStockPage * PAGE_SIZE)
                                    const paginatedBranchTx = filteredBranchTx.slice((branchTxPage - 1) * PAGE_SIZE, branchTxPage * PAGE_SIZE)


                                    return (
                                        <div
                                            key={branch.branch_id}
                                            className={`bg-white rounded-xl border transition-all duration-200 shadow-sm overflow-hidden ${isExpanded ? 'border-[#A0826D]' : 'border-gray-200'
                                                }`}
                                        >
                                            {/* Card Header — always visible, clickable */}
                                            <button
                                                onClick={() => handleToggleBranch(branch.branch_id)}
                                                className="w-full px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 hover:bg-gray-50 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    {/* Branch icon */}
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isExpanded ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-500'
                                                        }`}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12c.263 0 .524-.06.767-.175a2 2 0 0 0 .65-.491c.186-.21.333-.46.433-.734s.15-.568.15-.864a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 12 9.736a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 16 9.736c0 .295.052.588.152.861s.248.521.434.73a2 2 0 0 0 .649.488a1.8 1.8 0 0 0 1.53 0a2 2 0 0 0 .65-.488c.185-.209.332-.457.433-.73s.152-.566.152-.861c0-.974-1.108-3.85-1.618-5.121A.98.98 0 0 0 17.466 4H6.456a.99.99 0 0 0-.93.645C5.045 5.962 4 8.905 4 9.736c.023.59.241 1.148.611 1.567c.37.418.865.667 1.389.697m0 0c.328 0 .651-.091.94-.266A2.1 2.1 0 0 0 7.66 11h.681a2.1 2.1 0 0 0 .718.734c.29.175.613.266.942.266s.651-.091.94-.266c.29-.174.537-.427.719-.734h.681a2.1 2.1 0 0 0 .719.734c.289.175.612.266.94.266c.329 0 .652-.091.942-.266c.29-.174.536-.427.718-.734h.681c.183.307.43.56.719.734c.29.174.613.266.941.266a1.8 1.8 0 0 0 1.06-.351M6 12a1.77 1.77 0 0 1-1.163-.476M5 12v7a1 1 0 0 0 1 1h2v-5h3v5h7a1 1 0 0 0 1-1v-7m-5 3v2h2v-2z" />
                                                        </svg>
                                                    </div>

                                                    {/* Branch name + stats */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-semibold text-gray-800 text-sm truncate">
                                                                {formatBranchName(branch.name)}
                                                            </p>
                                                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                {branchItems.length} ingredient{branchItems.length !== 1 ? 's' : ''} tracked
                                                            </span>
                                                            {(() => {
                                                                const hasPendingRestock = (branchRestockRequests[branch.branch_id]?.data || [])
                                                                    .some(r => r.status === 'pending')
                                                                return hasPendingRestock ? (
                                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
                                                                        Restock Requested
                                                                    </span>
                                                                ) : null
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Stock status badges */}
                                                <div
                                                    className="flex items-center gap-2 overflow-x-auto scrollbar-hide pl-14 sm:pl-0 -mx-6 px-6 sm:mx-0 sm:px-0 sm:flex-shrink-0"
                                                    onMouseDown={handleDragMouseDown}
                                                    onMouseMove={handleDragMouseMove}
                                                    onMouseUp={handleDragMouseUpOrLeave}
                                                    onMouseLeave={handleDragMouseUpOrLeave}
                                                    onWheel={handleHorizontalWheelScroll}
                                                >
                                                    {outCount > 0 && (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                                            {outCount} out
                                                        </span>
                                                    )}
                                                    {lowCount > 0 && (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                                            {lowCount} low
                                                        </span>
                                                    )}
                                                    {outCount === 0 && lowCount === 0 && branchItems.length > 0 && (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                            All good
                                                        </span>
                                                    )}
                                                    {branchItems.length === 0 && (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                                                            No stock tracked
                                                        </span>
                                                    )}
                                                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                                        {inCount} in stock
                                                    </span>

                                                    {/* Chevron */}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16" height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        className={`text-gray-400 transition-transform duration-200 ml-1 ${isExpanded ? 'rotate-180' : ''}`}
                                                    >
                                                        <path d="M6 9l6 6 6-6" />
                                                    </svg>
                                                </div>
                                            </button>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="border-t border-gray-100">
                                                    {/* Sub-tabs */}
                                                    <div className="px-6 pt-4 pb-0 flex items-center justify-between gap-4 flex-wrap">
                                                        <div className="flex mb-6 bg-[#f9fbfc] rounded-lg shadow-sm p-1 w-full sm:w-fit max-w-full overflow-x-auto scrollbar-hide"
                                                            onMouseDown={handleDragMouseDown}
                                                            onMouseMove={handleDragMouseMove}
                                                            onMouseUp={handleDragMouseUpOrLeave}
                                                            onMouseLeave={handleDragMouseUpOrLeave}
                                                            onWheel={handleHorizontalWheelScroll}
                                                        >
                                                            {['stock_levels', 'restock_prediction', 'stock_count_review', 'stock_transactions', 'restock_request'].map(tab => (
                                                                <button
                                                                    key={tab}
                                                                    onClick={() => {
                                                                        setBranchSubTab(prev => ({ ...prev, [branch.branch_id]: tab }))
                                                                        if (tab === 'restock_prediction') fetchBranchRestockPredictions(branch.branch_id)
                                                                        if (tab === 'stock_transactions') fetchBranchTransactions(branch.branch_id)
                                                                        if (tab === 'restock_request') fetchBranchRestockRequests(branch.branch_id)
                                                                        if (tab === 'stock_count_review') fetchBranchStockCountReview(branch.branch_id, stockCountReviewDates[branch.branch_id] || new Date().toISOString().split('T')[0])
                                                                    }}
                                                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-shrink-0 whitespace-nowrap ${activeSubTab === tab
                                                                        ? 'bg-[#A0826D] text-white shadow-sm'
                                                                        : 'text-gray-600 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    {tab === 'stock_levels' ? 'Stock Levels' :
                                                                        tab === 'restock_prediction' ? 'Restock Prediction' :
                                                                            tab === 'stock_count_review' ? 'Stock Count Review' :
                                                                                tab === 'stock_transactions' ? 'Stock Transactions' :
                                                                                    'Restock Requests'}
                                                                    {tab === 'restock_request' && (() => {
                                                                        const pendingCount = (branchRestockRequests[branch.branch_id]?.data || [])
                                                                            .filter(r => r.status === 'pending').length
                                                                        return pendingCount > 0
                                                                            ? <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{pendingCount}</span>
                                                                            : null
                                                                    })()}
                                                                </button>
                                                            ))}
                                                        </div>

                                        {/* Search for active sub-tab — only for stock_levels and stock_transactions */}
                                        {(activeSubTab === 'stock_levels' || activeSubTab === 'stock_transactions') && (
                                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                                <div className="relative w-full sm:w-64">
                                                    <input
                                                        type="text"
                                                        placeholder={activeSubTab === 'stock_levels' ? 'Search ingredients...' : 'Search transactions...'}
                                                        value={activeSubTab === 'stock_levels' ? stockSearch : txSearch}
                                                        onChange={(e) => {
                                                            if (activeSubTab === 'stock_levels') {
                                                                setBranchStockSearch(prev => ({ ...prev, [branch.branch_id]: e.target.value }))
                                                            } else {
                                                                setBranchTxSearch(prev => ({ ...prev, [branch.branch_id]: e.target.value }))
                                                            }
                                                        }}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                    />
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                        </svg>
                                                    </span>
                                                </div>

                                                {activeSubTab === 'stock_levels' && (
                                                    <select
                                                        value={stockStatus}
                                                        onChange={(e) => {
                                                            setBranchStockStatusFilter(prev => ({ ...prev, [branch.branch_id]: e.target.value }))
                                                            setBranchStockPages(prev => ({ ...prev, [branch.branch_id]: 1 }))
                                                        }}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                    >
                                                        <option value="all">All Statuses</option>
                                                        <option value="in_stock">In Stock</option>
                                                        <option value="low_stock">Low Stock</option>
                                                        <option value="out_of_stock">Out of Stock</option>
                                                    </select>
                                                )}
                                            </div>
                                        )}
                                                    </div>

                                                    {/* Stock Levels Table */}
                                                    {activeSubTab === 'stock_levels' && (
                                                        <div className="overflow-x-auto mt-3">
                                                            <table className="w-full">
                                                                <thead className="bg-gray-50 border-y border-gray-100">
                                                                    <tr>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">On Hand</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reserved</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Level</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="bg-white divide-y divide-gray-100">
                                                                    {filteredBranchStock.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan="8" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                                {stockSearch ? 'No ingredients match your search.' : 'No stock records for this branch.'}
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        paginatedBranchStock.map(item => (
                                                                            <tr
                                                                                key={item.id}
                                                                                className={`hover:bg-gray-50 ${item.status === 'out_of_stock' ? 'bg-red-50/40' :
                                                                                    item.status === 'low_stock' ? 'bg-orange-50/40' : ''
                                                                                    }`}
                                                                            >
                                                                                <td className="px-6 py-3">
                                                                                    <p className="text-sm font-medium text-gray-800">{item.ingredient_name}</p>
                                                                                    <p className="text-xs text-gray-500">{item.unit}</p>
                                                                                </td>
                                                                                <td className="px-6 py-3 text-sm text-gray-600">{item.category}</td>
                                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-medium">
                                                                                        {item.current_stock} {item.unit}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${item.reserved_stock > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
                                                                                        }`}>
                                                                                        {item.reserved_stock} {item.unit}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${item.available_stock <= 0 ? 'bg-red-100 text-red-800' :
                                                                                        item.available_stock <= item.reorder_level ? 'bg-orange-100 text-orange-800' :
                                                                                            'bg-green-100 text-green-800'
                                                                                        }`}>
                                                                                        {item.available_stock} {item.unit}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                                        {item.reorder_level} {item.unit}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${item.status === 'out_of_stock' ? 'bg-red-100 text-red-800' :
                                                                                        item.status === 'low_stock' ? 'bg-orange-100 text-orange-800' :
                                                                                            'bg-green-100 text-green-800'
                                                                                        }`}>
                                                                                        {item.status === 'out_of_stock' ? 'Out of Stock' :
                                                                                            item.status === 'low_stock' ? 'Low Stock' : 'In Stock'}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                                                    {formatDateTime(item.last_updated)}
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                            <Pagination
                                                                page={branchStockPage}
                                                                totalItems={filteredBranchStock.length}
                                                                pageSize={PAGE_SIZE}
                                                                onPageChange={(p) => setBranchStockPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Stock Transactions Table */}
                                                    {activeSubTab === 'stock_transactions' && (
                                                        <div className="overflow-x-auto mt-3">
                                                            {txData?.loading ? (
                                                                <div className="flex items-center justify-center py-12">
                                                                    <div className="text-center">
                                                                        <div className="w-6 h-6 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                                                        <p className="text-sm text-gray-500">Loading transactions...</p>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <table className="w-full">
                                                                    <thead className="bg-gray-50 border-y border-gray-100">
                                                                        <tr>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Type</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference Type</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                                        {filteredBranchTx.length === 0 ? (
                                                                            <tr>
                                                                                <td colSpan="6" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                                    {txSearch ? 'No transactions match your search.' : 'No transactions found for this branch.'}
                                                                                </td>
                                                                            </tr>
                                                                        ) : (
                                                                            paginatedBranchTx.map(tx => (
                                                                                <tr key={tx.inventory_transaction_id} className="hover:bg-gray-50">
                                                                                    <td className="px-6 py-3">
                                                                                        <p className="text-sm font-medium text-gray-800">{tx.ingredient_name}</p>
                                                                                        <p className="text-xs text-gray-500">{tx.ingredient_category}</p>
                                                                                    </td>
                                                                                    <td className="px-6 py-3">
                                                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tx.transaction_type === 'stock_in' ? 'bg-green-100 text-green-700' :
                                                                                            tx.transaction_type === 'stock_out' ? 'bg-red-100 text-red-700' :
                                                                                                tx.transaction_type === 'sale_usage' ? 'bg-blue-100 text-blue-700' :
                                                                                                    tx.transaction_type === 'waste' ? 'bg-orange-100 text-orange-700' :
                                                                                                        'bg-gray-100 text-gray-700'
                                                                                            }`}>
                                                                                            {tx.transaction_type.replace(/_/g, ' ')}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-6 py-3 text-sm font-semibold text-gray-800">
                                                                                        {tx.quantity} {tx.unit}
                                                                                    </td>
                                                                                    <td className="px-6 py-3 text-sm text-gray-600">{tx.reference_type}</td>
                                                                                    <td className="px-6 py-3 text-sm text-gray-600">{tx.notes || '—'}</td>
                                                                                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDateTime(tx.created_at)}</td>
                                                                                </tr>
                                                                            ))
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                            <Pagination
                                                                page={branchTxPage}
                                                                totalItems={filteredBranchTx.length}
                                                                pageSize={PAGE_SIZE}
                                                                onPageChange={(p) => setBranchTxPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Stock Count Review — read-only view for super admin */}
                                                    {activeSubTab === 'stock_count_review' && (() => {
                                                        const reviewData = branchStockCountReview[branch.branch_id]
                                                        const currentDate = stockCountReviewDates[branch.branch_id] || new Date().toISOString().split('T')[0]

                                                        return (
                                                            <div>
                                                                {/* Date picker + refresh */}
                                                                <div className="px-6 pt-4 pb-2 flex items-center gap-3">
                                                                    <input
                                                                        type="date"
                                                                        value={currentDate}
                                                                        max={new Date().toISOString().split('T')[0]}
                                                                        onChange={(e) => {
                                                                            const newDate = e.target.value
                                                                            setStockCountReviewDates(prev => ({ ...prev, [branch.branch_id]: newDate }))
                                                                            fetchBranchStockCountReview(branch.branch_id, newDate)
                                                                            setBranchStockCountPages(prev => ({ ...prev, [branch.branch_id]: 1 }))
                                                                        }}
                                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                                    />
                                                                    <button
                                                                        onClick={() => fetchBranchStockCountReview(branch.branch_id, currentDate)}
                                                                        className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b]"
                                                                    >
                                                                        Refresh
                                                                    </button>
                                                                </div>

                                                                {/* Table */}
                                                                {reviewData?.loading || !reviewData ? (
                                                                    <div className="flex items-center justify-center py-12">
                                                                        <div className="text-center">
                                                                            <div className="w-6 h-6 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                                                            <p className="text-sm text-gray-500">Loading review data...</p>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="overflow-x-auto mt-2">
                                                                        <table className="w-full">
                                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                                <tr>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beg. Stock</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducted</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Qty</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff Count</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Counted By</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                                {reviewData.data.length === 0 ? (
                                                                                    <tr>
                                                                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                                            No inventory data found for this date.
                                                                                        </td>
                                                                                    </tr>
                                                                                ) : (
                                                                                    reviewData.data
                                                                                        .slice(
                                                                                            ((branchStockCountPages[branch.branch_id] || 1) - 1) * PAGE_SIZE,
                                                                                            (branchStockCountPages[branch.branch_id] || 1) * PAGE_SIZE
                                                                                        )
                                                                                        .map(row => {
                                                                                            const hasCount = row.staff_count !== null
                                                                                            const isOver = hasCount && row.variance > 0
                                                                                            const isShort = hasCount && row.variance < 0
                                                                                            const isMatch = hasCount && row.variance === 0

                                                                                            return (
                                                                                                <tr key={row.ingredient_id} className="hover:bg-gray-50">
                                                                                                    <td className="px-6 py-3">
                                                                                                        <p className="text-sm font-medium text-gray-800">{row.name}</p>
                                                                                                        <p className="text-xs text-gray-500">{row.unit} • {row.category}</p>
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                                                                                                            {row.beginning_stock} {row.unit}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                                                                                                            +{row.added_stock} {row.unit}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-medium">
                                                                                                            -{row.deducted_stock} {row.unit}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                                                                                                            {row.system_quantity} {row.unit}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        {hasCount ? (
                                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-sm font-medium">
                                                                                                                {row.staff_count} {row.unit}
                                                                                                            </span>
                                                                                                        ) : (
                                                                                                            <span className="text-xs text-gray-400 italic">No count yet</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        {hasCount ? (
                                                                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${isMatch ? 'bg-green-100 text-green-800' :
                                                                                                                isOver ? 'bg-blue-100 text-blue-800' :
                                                                                                                    'bg-red-100 text-red-800'
                                                                                                                }`}>
                                                                                                                {row.variance > 0 ? '+' : ''}{row.variance} {row.unit}
                                                                                                            </span>
                                                                                                        ) : (
                                                                                                            <span className="text-xs text-gray-400">—</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td className="px-6 py-3">
                                                                                                        {row.counted_by ? (
                                                                                                            <div>
                                                                                                                <p className="text-sm text-gray-700">{row.counted_by}</p>
                                                                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                                                                    {row.count_time
                                                                                                                        ? new Date(row.count_time).toLocaleString('en-PH', { hour: 'numeric', minute: '2-digit' })
                                                                                                                        : ''}
                                                                                                                </p>
                                                                                                            </div>
                                                                                                        ) : (
                                                                                                            <span className="text-xs text-gray-400 italic">—</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            )
                                                                                        })
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                        <Pagination
                                                                            page={branchStockCountPages[branch.branch_id] || 1}
                                                                            totalItems={reviewData.data.length}
                                                                            pageSize={PAGE_SIZE}
                                                                            onPageChange={(p) => setBranchStockCountPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )
                                                    })()}

                                                    {activeSubTab === 'restock_prediction' && (() => {
                                                        const predictionData = branchRestockPredictions[branch.branch_id]
                                                        const search = restockPredictionSearch[branch.branch_id] || ''
                                                        const isRecalculating = recalculatingPredictions[branch.branch_id]

                                                        if (predictionData?.loading || !predictionData) {
                                                            return (
                                                                <div className="flex items-center justify-center py-12">
                                                                    <div className="text-center">
                                                                        <div className="w-6 h-6 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                                                        <p className="text-sm text-gray-500">Loading predictions...</p>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }

                                                        const filtered = (predictionData.data || []).filter(item => {
                                                            const q = search.toLowerCase()
                                                            return item.ingredient_name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q)
                                                        })

                                                        const page = branchRestockPredictionPages[branch.branch_id] || 1
                                                        const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

                                                        return (
                                                            <div>
                                                                <div className="px-6 pt-4 pb-2 flex items-center justify-between gap-3 flex-wrap">
                                                                    <div className="relative w-full sm:w-64">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search ingredients..."
                                                                            value={search}
                                                                            onChange={(e) => setRestockPredictionSearch(prev => ({ ...prev, [branch.branch_id]: e.target.value }))}
                                                                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleRecalculatePredictions(branch.branch_id)}
                                                                        disabled={isRecalculating}
                                                                        className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b] disabled:opacity-50"
                                                                    >
                                                                        {isRecalculating ? 'Calculating...' : '↻ Recalculate (last 7 days)'}
                                                                    </button>
                                                                </div>

                                                                <div className="overflow-x-auto mt-2">
                                                                    <table className="w-full">
                                                                        <thead className="bg-gray-50 border-y border-gray-100">
                                                                            <tr>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock ~7 Days Ago</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weekly Usage</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buffer %</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suggested Restock</th>
                                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Calculated</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white divide-y divide-gray-100">
                                                                            {paginated.length === 0 ? (
                                                                                <tr>
                                                                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                                        No predictions yet. Click "Recalculate" to generate them.
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                paginated.map(item => (
                                                                                    <tr key={item.id} className="hover:bg-gray-50">
                                                                                        <td className="px-6 py-3">
                                                                                            <p className="text-sm font-medium text-gray-800">{item.ingredient_name}</p>
                                                                                            <p className="text-xs text-gray-500">{item.category}</p>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 text-sm text-gray-600">{item.stock_last_week} {item.unit}</td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-medium">
                                                                                                {item.weekly_usage} {item.unit}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 text-sm text-gray-600">{item.current_stock} {item.unit}</td>
                                                                                        <td className="px-6 py-3 text-sm text-gray-600">{item.buffer_percent}%</td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
                                                                                                {item.suggested_restock_qty.toFixed(0)} {item.unit}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(item.calculated_at)}</td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                    <Pagination
                                                                        page={page}
                                                                        totalItems={filtered.length}
                                                                        pageSize={PAGE_SIZE}
                                                                        onPageChange={(p) => setBranchRestockPredictionPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )
                                                    })()}

                                                    {/* Restock Requests */}
                                                    {activeSubTab === 'restock_request' && (() => {
                                                        const restockData = branchRestockRequests[branch.branch_id]

                                                        if (restockData?.loading || !restockData) {
                                                            return (
                                                                <div className="flex items-center justify-center py-12">
                                                                    <div className="text-center">
                                                                        <div className="w-6 h-6 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                                                        <p className="text-sm text-gray-500">Loading requests...</p>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }

                                                        const requests = restockData.data || []
                                                        const restockStatus = branchRestockStatusFilter[branch.branch_id] || 'all'
                                                        const filteredRequests = restockStatus === 'all'
                                                            ? requests
                                                            : requests.filter(r => r.status === restockStatus)
                                                        const branchRestockPage = branchRestockPages[branch.branch_id] || 1
                                                        const paginatedRestocks = filteredRequests.slice((branchRestockPage - 1) * PAGE_SIZE, branchRestockPage * PAGE_SIZE)

                                                        return (
                                                            <div className="mt-3">
                                                                <div className="px-6 pb-3 flex items-center justify-between gap-3 flex-wrap">
                                                                    <div className="flex gap-3 flex-wrap">
                                                                        {[
                                                                            { label: 'Pending', key: 'pending', color: 'bg-yellow-100 text-yellow-700' },
                                                                            { label: 'Approved', key: 'approved', color: 'bg-green-100 text-green-700' },
                                                                            { label: 'Received', key: 'fulfilled', color: 'bg-blue-100 text-blue-700' },
                                                                            { label: 'Rejected', key: 'rejected', color: 'bg-red-100 text-red-700' },
                                                                        ].map(({ label, key, color }) => (
                                                                            <span key={key} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${color}`}>
                                                                                {label}: {requests.filter(r => r.status === key).length}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                    <select
                                                                        value={restockStatus}
                                                                        onChange={(e) => {
                                                                            setBranchRestockStatusFilter(prev => ({ ...prev, [branch.branch_id]: e.target.value }))
                                                                            setBranchRestockPages(prev => ({ ...prev, [branch.branch_id]: 1 }))
                                                                        }}
                                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                                    >
                                                                        <option value="all">All Statuses</option>
                                                                        <option value="pending">Pending</option>
                                                                        <option value="approved">Approved</option>
                                                                        <option value="fulfilled">Received</option>
                                                                        <option value="rejected">Rejected</option>
                                                                    </select>
                                                                </div>
                                                                <div className="overflow-x-auto">
                                                                <table className="w-full">
                                                                    <thead className="bg-gray-50 border-y border-gray-100">
                                                                        <tr>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock at Request</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Level</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Qty</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                                        {filteredRequests.length === 0 ? (
                                                                            <tr>
                                                                                <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                                    {restockStatus === 'all' ? 'No restock requests from this branch.' : `No ${restockStatus} restock requests.`}
                                                                                </td>
                                                                            </tr>
                                                                        ) : (
                                                                            paginatedRestocks.map(req => {
                                                                                const isReviewing = reviewingRestockId === req.id
                                                                                return (
                                                                                    <tr key={req.id} className="hover:bg-gray-50">
                                                                                        <td className="px-6 py-3">
                                                                                            <p className="text-sm font-medium text-gray-800">{req.ingredient_name}</p>
                                                                                            <p className="text-xs text-gray-500">{req.unit}</p>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                                                                                                {req.current_stock} {req.unit}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                                                {req.reorder_level} {req.unit}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">
                                                                                                {req.requested_quantity} {req.unit}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                                req.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                                                    req.status === 'fulfilled' ? 'bg-blue-100 text-blue-800' :
                                                                                                        'bg-red-100 text-red-800'
                                                                                                }`}>
                                                                                                {req.status === 'pending' ? 'Pending' :
                                                                                                    req.status === 'approved' ? 'Approved' :
                                                                                                        req.status === 'fulfilled' ? 'Received' :
                                                                                                            'Rejected'}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                                                            {formatDateTime(req.created_at)}
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            {/* {req.status === 'pending' && (
                                                                                                <div className="flex items-center gap-2">
                                                                                                    <button
                                                                                                        onClick={() => handleApproveRestock(req.id, branch.branch_id, req.ingredient_name)}
                                                                                                        disabled={isReviewing}
                                                                                                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                                                                                    >
                                                                                                        Approve
                                                                                                    </button>
                                                                                                    <button
                                                                                                        onClick={() => handleRejectRestock(req.id, branch.branch_id, req.ingredient_name)}
                                                                                                        disabled={isReviewing}
                                                                                                        className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 disabled:opacity-50"
                                                                                                    >
                                                                                                        Reject
                                                                                                    </button>
                                                                                                </div>
                                                                                            )} */}
                                                                                            {(req.status === 'approved' || req.status === 'fulfilled' || req.status === 'pending' || req.status === 'rejected') && (
                                                                                                <span className="text-xs text-gray-400 italic">
                                                                                                    {req.status === 'approved' ? 'Waiting for branch receipt' :
                                                                                                        req.status === 'pending' ? 'Waiting for stock to be delivered' :
                                                                                                            req.status === 'fulfilled' ? 'Stock received by branch' :
                                                                                                                'Request rejected'}
                                                                                                </span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                                </div>
                                                                <Pagination
                                                                    page={branchRestockPage}
                                                                    totalItems={filteredRequests.length}
                                                                    pageSize={PAGE_SIZE}
                                                                    onPageChange={(p) => setBranchRestockPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                                />
                                                            </div>
                                                        )
                                                    })()}

                                                    {/* Bottom padding inside expanded card */}
                                                    <div className="h-4" />
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Ingredients Tab */}
                {activeTab === 'ingredients' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold text-gray-800">Ingredients</h2>
                                            <button
                                                onClick={() => {
                                                    setIngredientImportFile(null)
                                                    setIngredientImportPreview([])
                                                    setIngredientImportErrors([])
                                                    setShowIngredientImportModal(true)
                                                }}
                                                disabled={ingredientImporting}
                                                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                                Import CSV
                                            </button>
                                            <button
                                                onClick={exportIngredientsExcel}
                                                disabled={filteredIngredients.length === 0}
                                                className="px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-medium hover:bg-green-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">Manage raw materials used by menu items</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="relative w-64">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                    d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                            </svg>
                                        </span>
                                        <input
                                            type="text"
                                            placeholder="Search ingredients..."
                                            value={ingredientSearch}
                                            onChange={(e) => setIngredientSearch(e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                    </div>

                                    <button
                                        onClick={() => {
                                            fetchArchivedIngredients()
                                            setShowArchivedModal(true)
                                        }}
                                        className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                        title="View Archived"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                        </svg>
                                    </button>

                                    <button
                                        onClick={() => setShowAddIngredientModal(true)}
                                        className="px-4 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8c705b]"
                                    >
                                        + Add Ingredient
                                    </button>
                                </div>
                            </div>

                            {/* Category filter pills */}
                            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
                                onMouseDown={handleDragMouseDown}
                                onMouseMove={handleDragMouseMove}
                                onMouseUp={handleDragMouseUpOrLeave}
                                onMouseLeave={handleDragMouseUpOrLeave}
                                onWheel={handleHorizontalWheelScroll}
                            >
                                {['All', ...new Set(ingredients.map(i => i.category || '—').filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)).map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setIngredientCategoryFilter(cat)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 whitespace-nowrap ${ingredientCategoryFilter === cat
                                            ? 'bg-[#A0826D] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {cat}
                                        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${ingredientCategoryFilter === cat ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'
                                            }`}>
                                            {cat === 'All'
                                                ? ingredients.length
                                                : ingredients.filter(i => (i.category || '—') === cat).length}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price / Package</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredIngredients.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                No ingredients found.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedIngredients.map((ingredient) => (
                                            <tr key={ingredient.ingredient_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm text-gray-800 font-medium">{ingredient.name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{ingredient.unit}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{ingredient.category || '—'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {ingredient.package_label
                                                        ? `${ingredient.package_label}${ingredient.stock_per_package ? ` (${ingredient.stock_per_package} ${ingredient.unit})` : ''}`
                                                        : '—'}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {ingredient.package_price !== null && ingredient.package_price !== undefined
                                                        ? formatCurrency(ingredient.package_price)
                                                        : '—'}
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openEditIngredientModal(ingredient)}
                                                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Edit Ingredient"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
                                                            </svg>
                                                        </button>

                                                        <button
                                                            onClick={() => handleDeleteIngredient(ingredient)}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                            title="Delete Ingredient"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            <Pagination
                                page={ingredientsPage}
                                totalItems={filteredIngredients.length}
                                pageSize={PAGE_SIZE}
                                onPageChange={setIngredientsPage}
                            />
                        </div>
                    </div>
                )}

                {/* Recipe Tab */}
                {activeTab === 'recipes' && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold text-gray-800">Menu Item Recipes</h2>
                                            <button
                                                onClick={() => {
                                                    setRecipeImportFile(null)
                                                    setRecipeImportPreview([])
                                                    setRecipeImportErrors([])
                                                    setShowRecipeImportModal(true)
                                                }}
                                                disabled={recipeImporting}
                                                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                                Import CSV
                                            </button>
                                            <button
                                                onClick={exportRecipesExcel}
                                                disabled={filteredMenuItems.length === 0}
                                                className="px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-medium hover:bg-green-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">Set which ingredients and measurements are used per menu item</p>
                                    </div>
                                </div>

                                <div className="relative w-64">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search Menu items..."
                                        value={recipeItemSearch}
                                        onChange={(e) => setRecipeItemSearch(e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                </div>
                            </div>
                            {/* Category filter pills */}
                            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
                                onMouseDown={handleDragMouseDown}
                                onMouseMove={handleDragMouseMove}
                                onMouseUp={handleDragMouseUpOrLeave}
                                onMouseLeave={handleDragMouseUpOrLeave}
                                onWheel={handleHorizontalWheelScroll}
                            >
                                {['All', ...new Set(menuItems.map(i => i.category).filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)).map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => {
                                            setRecipeCategoryFilter(cat)
                                            setRecipeClassificationFilter('All')
                                            if (cat === 'All') {
                                                setAvailableRecipeClassifications([])
                                            } else {
                                                const classifications = [
                                                    ...new Set(
                                                        menuItems
                                                            .filter(item => item.category === cat)
                                                            .map(item => item.classification)
                                                            .filter(Boolean)
                                                    )
                                                ].sort()
                                                setAvailableRecipeClassifications(classifications)
                                            }
                                        }}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${recipeCategoryFilter === cat
                                            ? 'bg-[#A0826D] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {cat}
                                        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${recipeCategoryFilter === cat ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'
                                            }`}>
                                            {cat === 'All'
                                                ? menuItems.length
                                                : menuItems.filter(i => (i.category || '—') === cat).length}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Classification chips */}
                            {recipeCategoryFilter !== 'All' && availableRecipeClassifications.length > 0 && (
                                <div className="flex items-center gap-2 overflow-x-auto pb-1 pl-1 scrollbar-hide"
                                    onMouseDown={handleDragMouseDown}
                                    onMouseMove={handleDragMouseMove}
                                    onMouseUp={handleDragMouseUpOrLeave}
                                    onMouseLeave={handleDragMouseUpOrLeave}
                                    onWheel={handleHorizontalWheelScroll}
                                >
                                    <span className="text-xs text-gray-400 font-medium shrink-0">
                                        {recipeCategoryFilter}:
                                    </span>
                                    {['All', ...availableRecipeClassifications].map(cls => (
                                        <button
                                            key={cls}
                                            onClick={() => setRecipeClassificationFilter(cls)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all flex-shrink-0 whitespace-nowrap ${recipeClassificationFilter === cls
                                                ? 'bg-[#303030] border-[#303030] text-white'
                                                : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500'
                                                }`}
                                        >
                                            {cls === 'All' ? `All ${recipeCategoryFilter}` : cls}
                                            {cls !== 'All' && (
                                                <span className={`ml-1 ${recipeClassificationFilter === cls ? 'opacity-70' : 'text-gray-400'}`}>
                                                    ({menuItems.filter(i => i.category === recipeCategoryFilter && i.classification === cls).length})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Recipe status */}
                            <div className="inline-flex items-center rounded-full bg-gray-100 p-1 w-fit">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'set', label: 'Recipe Set' },
                                    { key: 'unset', label: 'No Recipe' },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => setRecipeSetFilter(key)}
                                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${recipeSetFilter === key
                                            ? key === 'unset'
                                                ? 'bg-orange-600 text-white shadow-sm'
                                                : key === 'set'
                                                    ? 'bg-emerald-600 text-white shadow-sm'
                                                    : 'bg-white text-gray-800 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        {label}
                                        <span className="ml-1.5 opacity-80">
                                            {recipeStatusLoading ? '…' : (
                                                key === 'all'
                                                    ? menuItems.length
                                                    : key === 'set'
                                                        ? menuItems.filter(i => itemsWithRecipeNames.has(i.name)).length
                                                        : menuItems.filter(i => !itemsWithRecipeNames.has(i.name)).length
                                            )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Menu Item</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classification</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredMenuItems.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                                No menu items found.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedMenuItems.map((item) => {
                                            const hasRecipe = itemsWithRecipeNames.has(item.name)
                                            return (
                                                <tr key={item.item_id}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-3">
                                                            {item.item_img ? (
                                                                <img
                                                                    src={item.item_img}
                                                                    alt={item.name}
                                                                    className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                                                                />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">
                                                                    N/A
                                                                </div>
                                                            )}
                                                            <p className="text-sm font-medium text-gray-800">{item.name}</p>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">{item.category}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">{item.classification}</td>
                                                    <td className="px-6 py-4">
                                                        {recipeStatusLoading ? (
                                                            <SkeletonBlock className="w-20 h-5" />
                                                        ) : hasRecipe ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                                                Recipe Set
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                                                No Recipe
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => openRecipeModal(item)}
                                                            className="px-3 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b]"
                                                        >
                                                            {hasRecipe ? 'Edit Ingredients' : 'Set Ingredients'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                            <Pagination
                                page={recipesPage}
                                totalItems={filteredMenuItems.length}
                                pageSize={PAGE_SIZE}
                                onPageChange={setRecipesPage}
                            />
                        </div>
                    </div>
                )}


                {/* POS Tab */}
                {activeTab === 'pos' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200 space-y-4">
                                {/* Title row */}
                                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800">POS Transactions</h2>
                                        <p className="text-sm text-gray-500 mt-1">Track recorded POS payments across all branches</p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 xl:items-center xl:flex-shrink-0 flex-wrap">
                                        <select
                                            value={dateFilter}
                                            onChange={(e) => {
                                                setDateFilter(e.target.value)
                                                if (e.target.value !== 'custom') {
                                                    setCustomStart('')
                                                    setCustomEnd('')
                                                }
                                            }}
                                            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        >
                                            <option value="all">All Time</option>
                                            <option value="day">Today</option>
                                            <option value="week">This Week</option>
                                            <option value="month">This Month</option>
                                            <option value="year">This Year</option>
                                            <option value="custom">Custom Range</option>
                                        </select>

                                        <select
                                            value={orderStatusFilter}
                                            onChange={(e) => setOrderStatusFilter(e.target.value)}
                                            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        >
                                            <option value="all">All Order Statuses</option>
                                            <option value="completed">Completed</option>
                                            <option value="pending">Pending</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>

                                        {dateFilter === 'custom' && (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs text-gray-400">From:</span>
                                                <input
                                                    type="date"
                                                    value={customStart}
                                                    max={customEnd || new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => setCustomStart(e.target.value)}
                                                    className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                    style={{ borderColor: customStart && customEnd ? '#A0826D' : '#D1D5DB' }}
                                                />
                                                <span className="text-xs text-gray-400">To</span>
                                                <input
                                                    type="date"
                                                    value={customEnd}
                                                    min={customStart}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => setCustomEnd(e.target.value)}
                                                    className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                    style={{ borderColor: customStart && customEnd ? '#A0826D' : '#D1D5DB' }}
                                                />
                                                {customStart && customEnd && (
                                                    <button
                                                        onClick={() => { setCustomStart(''); setCustomEnd('') }}
                                                        className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50"
                                                    >
                                                        ✕ Clear
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        <div className="relative w-full sm:w-72">
                                            <input
                                                type="text"
                                                placeholder="Search transactions..."
                                                value={transactionSearch}
                                                onChange={(e) => setTransactionSearch(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] text-sm"
                                            />
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Branch pills */}
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                    <button
                                        onClick={() => setSelectedBranchFilter('all')}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all flex-shrink-0 whitespace-nowrap ${selectedBranchFilter === 'all'
                                            ? 'bg-[#A0826D] border-[#A0826D] text-white shadow-sm'
                                            : 'bg-white border-gray-200 text-gray-700 hover:border-[#A0826D] hover:text-[#A0826D]'
                                            }`}
                                    >
                                        <span>All Branches</span>
                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${selectedBranchFilter === 'all' ? 'bg-white text-[#A0826D]' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {posTransactions.length}
                                        </span>
                                    </button>

                                    {branches.map(branch => {
                                        const isSelected = selectedBranchFilter === branch.branch_id
                                        const branchTxCount = posTransactions.filter(t => {
                                            return t.branch_name === formatBranchName(branch.name)
                                        }).length

                                        return (
                                            <button
                                                key={branch.branch_id}
                                                onClick={() => setSelectedBranchFilter(branch.branch_id)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all flex-shrink-0 whitespace-nowrap ${isSelected
                                                    ? 'bg-[#A0826D] border-[#A0826D] text-white shadow-sm'
                                                    : 'bg-white border-gray-200 text-gray-700 hover:border-[#A0826D] hover:text-[#A0826D]'
                                                    }`}
                                            >
                                                <span>{formatBranchName(branch.name)}</span>
                                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isSelected ? 'bg-white/30 text-white' : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {branchTxCount}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>

                                {/* Summary pills */}
                                <div className="flex gap-3 flex-wrap">
                                    {[
                                        { label: 'Completed', key: 'completed', color: 'bg-green-100 text-green-700' },
                                        { label: 'Pending', key: 'pending', color: 'bg-yellow-100 text-yellow-700' },
                                        { label: 'Cancelled', key: 'cancelled', color: 'bg-red-100 text-red-700' },
                                    ].map(({ label, key, color }) => (
                                        <button
                                            key={key}
                                            onClick={() => setOrderStatusFilter(prev => prev === key ? 'all' : key)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${color} ${orderStatusFilter === key ? 'ring-2 ring-offset-1 ring-[#A0826D]' : ''}`}
                                        >
                                            {label}:
                                            {posLoading
                                                ? <SkeletonBlock className="w-5 h-3" />
                                                : posTransactions.filter(t => t.order_status === key && (selectedBranchFilter === 'all' || t.branch_id === selectedBranchFilter)).length}
                                        </button>
                                    ))}
                                    {orderStatusFilter !== 'all' && (
                                        <button
                                            onClick={() => setOrderStatusFilter('all')}
                                            className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 bg-gray-200 text-gray-600 hover:bg-gray-300"
                                        >
                                            ✕ Clear filter
                                        </button>
                                    )}
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 bg-gray-100 text-gray-600">
                                        Total:
                                        {posLoading
                                            ? <SkeletonBlock className="w-8 h-3" />
                                            : `${filteredTransactions.length} transactions`}
                                    </span>
                                </div>
                            </div>


                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference #</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                                        </tr>
                                    </thead>

                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredTransactions.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                    No transactions found.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedPOSTransactions.map((transaction) => (
                                                <tr key={transaction.order_transaction_id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {transaction.branch_name}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                                                        {transaction.reference_number}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm text-gray-600 uppercase">
                                                        {transaction.payment_method}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                                                        {formatCurrency(transaction.transaction_amount)}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {transaction.payment_status}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {transaction.order_status}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {formatDateTime(transaction.created_at)}
                                                    </td>

                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                                <Pagination
                                    page={posTransactionsPage}
                                    totalItems={filteredTransactions.length}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={setPosTransactionsPage}
                                />
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                Note: This super admin page is currently best used for cross-branch summary and menu assignment visibility.
                                Detailed stock movement, actual inventory quantities, and operational stock updates should be handled in the branch manager/staff side with a proper inventory table.
                            </p>
                        </div>
                    </div>
                )}

                {/* Add Ingredient Modal */}
                {showAddIngredientModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => setShowAddIngredientModal(false)}
                        ></div>

                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Add Ingredient</h2>
                                <p className="text-sm text-gray-500 mt-1">Add a raw material used in your menu item recipes.</p>
                            </div>

                            <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Milk, Sugar, Espresso"
                                        value={ingredientFormData.name}
                                        maxLength={FIELD_LIMITS.name}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                name: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.name.length}/{FIELD_LIMITS.name}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. ml, grams, pcs"
                                        value={ingredientFormData.unit}
                                        maxLength={FIELD_LIMITS.unit}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                unit: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.unit.length}/{FIELD_LIMITS.unit}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Dairy, Sweetener"
                                        value={ingredientFormData.category}
                                        maxLength={FIELD_LIMITS.category}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                category: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.category.length}/{FIELD_LIMITS.category}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Package Label <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Bottle, Box"
                                        value={ingredientFormData.package_label}
                                        maxLength={FIELD_LIMITS.package_label}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                package_label: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.package_label.length}/{FIELD_LIMITS.package_label}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity per Package <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={ingredientFormData.stock_per_package}
                                        onChange={(e) => setIngredientFormData({ ...ingredientFormData, stock_per_package: e.target.value })}
                                        placeholder="e.g. 1000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Example: 1 box = 1000 ml
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Price per Package <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={ingredientFormData.package_price}
                                        onChange={(e) => setIngredientFormData({ ...ingredientFormData, package_price: e.target.value })}
                                        placeholder="e.g. 250.00"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Example: 1 bottle = ₱250.00
                                    </p>
                                </div>
                            </div>



                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                                <button
                                    onClick={() => {
                                        setEditingIngredient(null)
                                        setIngredientFormData({
                                            name: '',
                                            unit: '',
                                            category: '',
                                            package_label: '',
                                            stock_per_package: '',
                                            package_price: ''
                                        })
                                        setShowAddIngredientModal(false)
                                        setEditingIngredient(null)
                                    }}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddIngredient}
                                    className="px-6 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8c705b]"
                                >
                                    Add Ingredient
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Ingredient Modal */}
                {showEditIngredientModal && editingIngredient && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => {
                                setShowEditIngredientModal(false)
                                setEditingIngredient(null)
                                setIngredientFormData({
                                    name: '',
                                    unit: '',
                                    category: '',
                                    package_label: '',
                                    stock_per_package: '',
                                    package_price: ''
                                })
                            }}
                        ></div>

                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Edit Ingredient</h2>
                                <p className="text-sm text-gray-500 mt-1">Update this ingredient's details, unit, or packaging info.</p>
                            </div>

                            <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={ingredientFormData.name}
                                        maxLength={FIELD_LIMITS.name}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                name: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.name.length}/{FIELD_LIMITS.name}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={ingredientFormData.unit}
                                        maxLength={FIELD_LIMITS.unit}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                unit: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.unit.length}/{FIELD_LIMITS.unit}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={ingredientFormData.category}
                                        maxLength={FIELD_LIMITS.category}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                category: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.category.length}/{FIELD_LIMITS.category}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Package Label <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={ingredientFormData.package_label}
                                        maxLength={FIELD_LIMITS.package_label}
                                        onChange={(e) =>
                                            setIngredientFormData({
                                                ...ingredientFormData,
                                                package_label: e.target.value
                                            })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-right">
                                        {ingredientFormData.package_label.length}/{FIELD_LIMITS.package_label}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity per Package <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={ingredientFormData.stock_per_package}
                                        onChange={(e) => setIngredientFormData({ ...ingredientFormData, stock_per_package: e.target.value })}
                                        placeholder="e.g. 1000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Example: 1 box = 1000 ml
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Price per Package <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={ingredientFormData.package_price}
                                        onChange={(e) => setIngredientFormData({ ...ingredientFormData, package_price: e.target.value })}
                                        placeholder="e.g. 250.00"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Example: 1 bottle = ₱250.00
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                                <button
                                    onClick={() => {
                                        setShowEditIngredientModal(false)
                                        setEditingIngredient(null)
                                        setIngredientFormData({
                                            name: '',
                                            unit: '',
                                            category: '',
                                            package_label: '',
                                            stock_per_package: '',
                                            package_price: ''
                                        })
                                    }}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateIngredient}
                                    disabled={!hasIngredientChanges}
                                    className={`px-6 py-2 rounded-lg font-medium ${hasIngredientChanges
                                        ? 'bg-[#A0826D] text-white hover:bg-[#8c705b]'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Ingredient Import Modal */}
                {showIngredientImportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowIngredientImportModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Import Ingredients CSV</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Required columns: <code className="bg-gray-100 px-1 rounded text-xs">name, unit</code> — Optional: <code className="bg-gray-100 px-1 rounded text-xs">category, package_label, stock_per_package, package_price</code>
                                </p>
                            </div>

                            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0 flex items-center gap-4 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                    {ingredientImportFile ? ingredientImportFile.name : 'Choose CSV File'}
                                    <input type="file" accept=".xlsx" onChange={handleIngredientImportFileChange} className="hidden" />
                                </label>
                                {ingredientImportFile && (
                                    <button onClick={() => { setIngredientImportFile(null); setIngredientImportPreview([]); setIngredientImportErrors([]) }}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">✕</button>
                                )}
                                {ingredientImportPreview.length > 0 && (
                                    <div className="flex gap-3 text-sm">
                                        <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                                            {ingredientImportPreview.filter(r => !r._hasError).length} valid
                                        </span>
                                        {ingredientImportErrors.length > 0 && (
                                            <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                                {ingredientImportErrors.length} with errors
                                            </span>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => downloadExcelTemplate(
                                        'ingredients_template.xlsx',
                                        'Ingredients',
                                        ['name', 'unit', 'category', 'package_label', 'stock_per_package', 'package_price'],
                                        [
                                            ['Espresso Beans', 'g', 'Coffee', 'Bag', 1000, 850],
                                            ['Whole Milk', 'ml', 'Dairy', 'Carton', 1000, 95],
                                            ['Vanilla Syrup', 'ml', 'Syrup', 'Bottle', 750, 320],
                                        ]
                                    )}
                                    className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                    Download Template
                                </button>
                            </div>

                            {ingredientImportErrors.length > 0 && (
                                <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex-shrink-0 max-h-28 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Errors — these rows will be skipped:</p>
                                    {ingredientImportErrors.map((e, i) => (
                                        <p key={i} className="text-xs text-red-600">Row {e.row} ({e.name}): {e.errors.join('; ')}</p>
                                    ))}
                                </div>
                            )}

                            <div className="overflow-y-auto flex-1">
                                {ingredientImportPreview.length === 0 ? (
                                    <div className="px-6 py-12 text-center text-gray-400 text-sm">Upload a CSV file to preview before importing.</div>
                                ) : (
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price/Pkg</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {ingredientImportPreview.map((row, i) => {
                                                const isDupe = !row._hasError && ingredients.some(ing => ing.name.trim().toLowerCase() === row.name.toLowerCase())
                                                return (
                                                    <tr key={i} className={row._hasError ? 'bg-red-50' : isDupe ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-4 py-2.5">
                                                            {row._hasError ? <span className="text-xs font-semibold text-red-600">Error</span>
                                                                : isDupe ? <span className="text-xs font-semibold text-amber-600">Duplicate</span>
                                                                    : <span className="text-xs font-semibold text-green-600">Ready</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{row.name}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{row.unit}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{row.category || '—'}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{row.package_label || '—'}{row.stock_per_package ? ` (${row.stock_per_package})` : ''}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{row.package_price != null ? `₱${row.package_price}` : '—'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <p className="text-xs text-gray-400">Duplicates and error rows are skipped automatically.</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setShowIngredientImportModal(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                                    <button
                                        onClick={handleIngredientImport}
                                        disabled={ingredientImporting || ingredientImportPreview.filter(r => !r._hasError).length === 0}
                                        className={`px-5 py-2 rounded-lg font-medium ${ingredientImporting || ingredientImportPreview.filter(r => !r._hasError).length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                                    >
                                        {ingredientImporting ? 'Importing...' : `Import ${ingredientImportPreview.filter(r => !r._hasError && !ingredients.some(ing => ing.name.trim().toLowerCase() === r.name.toLowerCase())).length} Ingredients`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Recipe Import Modal */}
                {showRecipeImportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowRecipeImportModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Import Recipes CSV</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Required columns: <code className="bg-gray-100 px-1 rounded text-xs">item_name, ingredient_name, quantity_used</code>
                                </p>
                                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                                    Importing will <strong>replace</strong> existing recipes for each affected menu item. Item and ingredient names must exactly match what's in the system.
                                </div>
                            </div>

                            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0 flex items-center gap-4 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                    {recipeImportFile ? recipeImportFile.name : 'Choose CSV File'}
                                    <input type="file" accept=".xlsx" onChange={handleRecipeImportFileChange} className="hidden" />
                                </label>
                                {recipeImportFile && (
                                    <button onClick={() => { setRecipeImportFile(null); setRecipeImportPreview([]); setRecipeImportErrors([]) }}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">✕</button>
                                )}
                                {recipeImportPreview.length > 0 && (
                                    <div className="flex gap-3 text-sm">
                                        <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                                            {recipeImportPreview.filter(r => !r._hasError).length} valid
                                        </span>
                                        {recipeImportErrors.length > 0 && (
                                            <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                                {recipeImportErrors.length} with errors
                                            </span>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => downloadExcelTemplate(
                                        'recipes_template.xlsx',
                                        'Recipes',
                                        ['item_name', 'ingredient_name', 'quantity_used'],
                                        [
                                            ['Cappuccino', 'Espresso Beans', 18],
                                            ['Cappuccino', 'Whole Milk', 150],
                                            ['Latte', 'Espresso Beans', 18],
                                            ['Latte', 'Whole Milk', 200],
                                        ]
                                    )}
                                    className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                    Download Template
                                </button>
                            </div>

                            {recipeImportErrors.length > 0 && (
                                <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex-shrink-0 max-h-28 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Errors — these rows will be skipped:</p>
                                    {recipeImportErrors.map((e, i) => (
                                        <p key={i} className="text-xs text-red-600">Row {e.row} ({e.name}): {e.errors.join('; ')}</p>
                                    ))}
                                </div>
                            )}

                            <div className="overflow-y-auto flex-1">
                                {recipeImportPreview.length === 0 ? (
                                    <div className="px-6 py-12 text-center text-gray-400 text-sm">Upload a CSV file to preview before importing.</div>
                                ) : (
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Menu Item</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Used</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {recipeImportPreview.map((row, i) => {
                                                const itemFound = menuItems.some(m => m.name.trim().toLowerCase() === row.item_name.toLowerCase())
                                                const matchedIngredient = ingredients.find(ing => ing.name.trim().toLowerCase() === row.ingredient_name.toLowerCase())
                                                const ingFound = !!matchedIngredient
                                                const canResolve = !row._hasError && itemFound && ingFound
                                                return (
                                                    <tr key={i} className={row._hasError ? 'bg-red-50' : !canResolve ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-4 py-2.5">
                                                            {row._hasError ? <span className="text-xs font-semibold text-red-600">Error</span>
                                                                : !canResolve ? <span className="text-xs font-semibold text-amber-600">Not Found</span>
                                                                    : <span className="text-xs font-semibold text-green-600">Ready</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                                                            {row.item_name}
                                                            {!row._hasError && !itemFound && <span className="ml-1 text-xs text-red-500">(not in menu)</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">
                                                            {row.ingredient_name}
                                                            {!row._hasError && !ingFound && <span className="ml-1 text-xs text-red-500">(not in ingredients)</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{row.quantity_used || '—'}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">{matchedIngredient?.unit || '—'}</td>
                                                        <td className="px-4 py-2.5">
                                                            {canResolve
                                                                ? <span className="text-xs text-green-600">Both found</span>
                                                                : <span className="text-xs text-amber-600">Will be skipped</span>}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <p className="text-xs text-gray-400">Rows where the item or ingredient isn't found are skipped. Existing recipes for matched items are replaced.</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setShowRecipeImportModal(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                                    <button
                                        onClick={handleRecipeImport}
                                        disabled={recipeImporting || recipeImportPreview.filter(r => !r._hasError).length === 0}
                                        className={`px-5 py-2 rounded-lg font-medium ${recipeImporting || recipeImportPreview.filter(r => !r._hasError).length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                                    >
                                        {recipeImporting ? 'Importing...' : `Import ${recipeImportPreview.filter(r => {
                                            if (r._hasError) return false
                                            const itemFound = menuItems.some(m => m.name.trim().toLowerCase() === r.item_name.toLowerCase())
                                            const ingFound = ingredients.some(ing => ing.name.trim().toLowerCase() === r.ingredient_name.toLowerCase())
                                            return itemFound && ingFound
                                        }).length} Rows`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Assign Recipe Modal */}
                {showAssignRecipeModal && selectedMenuItem && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => {
                                setShowAssignRecipeModal(false)
                                setSelectedMenuItem(null)
                            }}
                        ></div>

                        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Set Ingredients</h2>
                                <p className="text-sm text-gray-500 mt-1">{selectedMenuItem.name}</p>
                            </div>

                            <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                                {recipeFormRows.map((row, index) => (
                                    <div key={index} className="grid grid-cols-12 gap-3 items-end">
                                        <div className="col-span-5 relative">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient <span className="text-red-500">*</span></label>

                                            <input
                                                ref={el => ingredientInputRefs.current[index] = el}
                                                type="text"
                                                value={
                                                    row.ingredient_id
                                                        ? ingredients.find(ingredient => String(ingredient.ingredient_id) === String(row.ingredient_id))?.name || ''
                                                        : ingredientDropdownSearch[index] || ''
                                                }
                                                onFocus={() => {
                                                    const rect = ingredientInputRefs.current[index]?.getBoundingClientRect()
                                                    if (rect) {
                                                        setDropdownPosition({
                                                            top: rect.bottom + window.scrollY,
                                                            left: rect.left + window.scrollX,
                                                            width: rect.width
                                                        })
                                                    }
                                                    setOpenIngredientDropdownIndex(index)
                                                }}
                                                onChange={(e) => {
                                                    updateRecipeFormRow(index, 'ingredient_id', '')
                                                    setIngredientDropdownSearch((prev) => ({
                                                        ...prev,
                                                        [index]: e.target.value
                                                    }))
                                                    const rect = ingredientInputRefs.current[index]?.getBoundingClientRect()
                                                    if (rect) {
                                                        setDropdownPosition({
                                                            top: rect.bottom + window.scrollY,
                                                            left: rect.left + window.scrollX,
                                                            width: rect.width
                                                        })
                                                    }
                                                    setOpenIngredientDropdownIndex(index)
                                                }}
                                                placeholder="Search and select ingredient..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>


                                        <div className="col-span-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Used <span className="text-red-500">*</span></label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                placeholder='e.g. 15, 250, 0.5'
                                                value={row.quantity_used}
                                                onChange={(e) => updateRecipeFormRow(index, 'quantity_used', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>

                                        <div className="col-span-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={ingredients.find(ingredient => ingredient.ingredient_id === row.ingredient_id)?.unit || ''}
                                                readOnly
                                                placeholder="Auto-filled from ingredient"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                            />
                                        </div>

                                        <div className="col-span-1">
                                            <button
                                                onClick={() => removeRecipeFormRow(index)}
                                                className="w-full px-2 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    onClick={addRecipeFormRow}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                                >
                                    + Add Row
                                </button>
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                                <button
                                    onClick={() => {
                                        setShowAssignRecipeModal(false)
                                        setSelectedMenuItem(null)
                                    }}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveRecipe}
                                    disabled={!hasRecipeChanges}
                                    className={`px-6 py-2 rounded-lg font-medium ${hasRecipeChanges
                                        ? 'bg-[#A0826D] text-white hover:bg-[#8c705b]'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                >
                                    Save Recipe
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Archived Ingredients Modal */}
                {showArchivedModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={closeArchivedModal}
                        ></div>

                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 space-y-3 flex-shrink-0">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800">
                                            Archived Ingredients
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            Reinstate archived ingredients back to active list
                                        </p>
                                    </div>
                                </div>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search archived ingredients..."
                                        value={archivedSearch}
                                        onChange={(e) => setArchivedSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                    {['All', ...new Set(archivedIngredients.map(i => i.category || '—').filter(Boolean))].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setArchivedCategoryFilter(cat)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${archivedCategoryFilter === cat
                                                ? 'bg-[#A0826D] text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {cat}
                                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${archivedCategoryFilter === cat
                                                ? 'bg-white/30 text-white'
                                                : 'bg-gray-200 text-gray-500'
                                                }`}>
                                                {cat === 'All'
                                                    ? archivedIngredients.length
                                                    : archivedIngredients.filter(i => (i.category || '—') === cat).length}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="overflow-y-auto">
                                {(() => {
                                    const filteredArchivedIngredients = archivedIngredients.filter(ingredient => {
                                        const query = archivedSearch.toLowerCase()
                                        const matchesSearch =
                                            String(ingredient.name || '').toLowerCase().includes(query) ||
                                            String(ingredient.unit || '').toLowerCase().includes(query) ||
                                            String(ingredient.category || '').toLowerCase().includes(query)
                                        const matchesCategory =
                                            archivedCategoryFilter === 'All' || (ingredient.category || '—') === archivedCategoryFilter
                                        return matchesSearch && matchesCategory
                                    })

                                    const pagedArchivedIngredients = filteredArchivedIngredients.slice(
                                        (archivedPage - 1) * PAGE_SIZE,
                                        archivedPage * PAGE_SIZE
                                    )

                                    return (
                                        <>
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            Ingredient
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            Unit
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            Category
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody className="divide-y divide-gray-200">
                                                    {filteredArchivedIngredients.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                                                {archivedSearch || archivedCategoryFilter !== 'All'
                                                                    ? 'No archived ingredients match your filters.'
                                                                    : 'No archived ingredients found.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        pagedArchivedIngredients.map((ingredient) => (
                                                            <tr key={ingredient.ingredient_id}>
                                                                <td className="px-6 py-4 text-sm font-medium text-gray-800">
                                                                    {ingredient.name}
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                                    {ingredient.unit}
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                                    {ingredient.category || '—'}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => handleReinstateIngredient(ingredient)}
                                                                            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                                                                        >
                                                                            Reinstate
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveIngredient(ingredient)}
                                                                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                                                                        >
                                                                            Remove Permanently
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                            <Pagination
                                                page={archivedPage}
                                                totalItems={filteredArchivedIngredients.length}
                                                pageSize={PAGE_SIZE}
                                                onPageChange={setArchivedPage}
                                            />
                                        </>
                                    )
                                })()}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
                                <button
                                    onClick={closeArchivedModal}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* Ingredient dropdown */}
            {openIngredientDropdownIndex !== null && showAssignRecipeModal && (() => {
                const index = openIngredientDropdownIndex
                const row = recipeFormRows[index]

                const filteredOptions = [...ingredients]
                    .filter((ingredient) => {
                        const selectedInOtherRows = recipeFormRows.some(
                            (recipeRow, recipeIndex) =>
                                recipeIndex !== index &&
                                String(recipeRow.ingredient_id) === String(ingredient.ingredient_id)
                        )
                        const query = String(ingredientDropdownSearch[index] || '').toLowerCase()
                        return (
                            !selectedInOtherRows &&
                            (
                                String(ingredient.name || '').toLowerCase().includes(query) ||
                                String(ingredient.unit || '').toLowerCase().includes(query) ||
                                String(ingredient.category || '').toLowerCase().includes(query)
                            )
                        )
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))

                return (
                    <div
                        ref={ingredientDropdownContainerRef}
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            zIndex: 9999
                        }}
                        className="max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
                    >
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No ingredients found.</div>
                        ) : (
                            filteredOptions.map((ingredient) => (
                                <button
                                    key={ingredient.ingredient_id}
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        updateRecipeFormRow(index, 'ingredient_id', ingredient.ingredient_id)
                                        setIngredientDropdownSearch((prev) => ({
                                            ...prev,
                                            [index]: ingredient.name
                                        }))
                                        setOpenIngredientDropdownIndex(null)
                                    }}
                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                >
                                    <div className="text-sm font-medium text-gray-800">{ingredient.name}</div>
                                    <div className="text-xs text-gray-500">
                                        {ingredient.unit || '—'}{ingredient.category ? ` • ${ingredient.category}` : ''}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )
            })()}

            <RecipeAISidebar
                open={aiSidebarOpen}
                onClose={() => setAiSidebarOpen(false)}
                summary={aiSummary}
                recommendations={aiRecommendations}
                loading={aiLoading}
                error={aiError}
                onGenerate={handleGenerateRecipeIdeas}
                hasData={branchStockLevels.length > 0}
                history={history}
                historyLoading={historyLoading}
                onDeleteHistory={deleteAnalysis}
                scope={recipeAIScope}
                onScopeChange={setRecipeAIScope}
                branches={branches}
            />
            <RecipeAIFloatingButton onClick={() => setAiSidebarOpen(true)} />
        </div>
    )
}